import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, ComposedChart, Line, ReferenceLine, Legend, BarChart, Bar, Cell, LineChart, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis } from 'recharts';
import MainLayout from '../../components/MainLayout';
import LSTMDefectAnalysis from '../../components/LSTMDefectAnalysis';
import MonitoringWatch1 from '../../components/MonitoringWatch1';
import MonitoringWatch2 from '../../components/MonitoringWatch2';
import {
  fetchMachineOverview, fetchMachineCycle, fetchMachineTemperature, fetchMachineDrive,
  fetchMachineAlarm,         // mock — 라즈베리파이 실 데이터는 설비현황판에서만 사용
  fetchEquipments,           // mock 사출기 목록
  fetchMachineSpc, fetchWidgetConfig, saveWidgetConfig, fetchMachineWidget, fetchCustomWidget,
  fetchShotHistory           // mock 샷 히스토리
} from '../../services/api';
import { getStoredUser } from '../../services/authService';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import './Machine.css';

function Machine() {
  const location = useLocation();
  const navigate = useNavigate();
  const machineData = location.state?.machineData;
  const openTab = location.state?.openTab; // 열어야 할 탭
  const alarmId = location.state?.alarmId; // 특정 알람 ID

  // machineData가 있으면 사용, 없으면 null (설비 목록에서 선택하도록)
  const machine = (machineData && machineData.id) ? machineData : null;

  // 사출기 목록 API 상태
  const [allMachines, setAllMachines] = useState([]);
  const [machinesLoading, setMachinesLoading] = useState(true);
  const [selectedMachineId, setSelectedMachineId] = useState(machine?.id || null);
  const [selectedMachine, setSelectedMachine] = useState(machine?.name || '');

  // machineData가 변경되고 유효한 id가 있을 때만 업데이트
  useEffect(() => {
    if (machineData && machineData.id) {
      setSelectedMachineId(machineData.id);
      setSelectedMachine(machineData.name);
    }
  }, [machineData]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('대시보드');
  const [selectedChart, setSelectedChart] = useState('전체');
  const [autoScale, setAutoScale] = useState(true);
  const [dataHold, setDataHold] = useState(false);
  const [isWidgetModalOpen, setIsWidgetModalOpen] = useState(false);
  const [selectedWidgetSlot, setSelectedWidgetSlot] = useState(null);
  const [showSizeSelector, setShowSizeSelector] = useState(false);
  const [selectedWidgetType, setSelectedWidgetType] = useState(null);
  const [dashboardWidgets, setDashboardWidgets] = useState([]);
  const dashboardWidgetsRef = useRef([]); // 최신 dashboardWidgets 참조
  const fetchAllWidgetDataRef = useRef(null); // fetchAllWidgetData 최신 참조
  const isFetchingWidgetDataRef = useRef(false); // 중복 fetch 방지 플래그
  const [showCustomWidgetBuilder, setShowCustomWidgetBuilder] = useState(false);
  const [customWidgetConfig, setCustomWidgetConfig] = useState({
    size: '1x1',
    title: '',
    chartType: 'line',
    xAxis: 'shotNumber',
    yAxis: 'pressure'
  });

  // Overview API 상태
  const [overviewApiData, setOverviewApiData] = useState(null);
  const [isOverviewLoading, setIsOverviewLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const refreshLabelRef = useRef(null);
  const refreshIconRef = useRef(null);

  // 현재 탭과 설비 ID를 ref로 저장 (useAutoRefresh 콜백에서 사용)
  const activeTabRef = useRef(activeTab);
  const selectedMachineIdRef = useRef(selectedMachineId);
  const dataHoldRef = useRef(dataHold);

  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  useEffect(() => {
    selectedMachineIdRef.current = selectedMachineId;
  }, [selectedMachineId]);

  useEffect(() => {
    dataHoldRef.current = dataHold;
  }, [dataHold]);

  // DOM 직접 조작으로 리프레시 표시 (리렌더링 방지)
  const setRefreshingUI = useCallback((isRefreshing) => {
    if (refreshLabelRef.current) {
      if (isRefreshing) {
        refreshLabelRef.current.classList.add('refreshing');
      } else {
        refreshLabelRef.current.classList.remove('refreshing');
      }
    }
    if (refreshIconRef.current) {
      if (isRefreshing) {
        refreshIconRef.current.classList.add('spinning');
      } else {
        refreshIconRef.current.classList.remove('spinning');
      }
    }
  }, []);

  // Cycle API 상태
  const [cycleData, setCycleData] = useState(null);
  const [isCycleLoading, setIsCycleLoading] = useState(false);
  const [cycleError, setCycleError] = useState(null);

  // Temperature API 상태
  const [temperatureData, setTemperatureData] = useState(null);
  const [isTemperatureLoading, setIsTemperatureLoading] = useState(false);
  const [temperatureError, setTemperatureError] = useState(null);

  // 양/불 탭 - 선택된 샷 번호 및 샷 데이터
  const [selectedShotNumber, setSelectedShotNumber] = useState(20);
  const [showLSTMAnalysis, setShowLSTMAnalysis] = useState(false);

  // Shot History API 상태
  const [shotHistoryData, setShotHistoryData] = useState([]);
  const [isShotHistoryLoading, setIsShotHistoryLoading] = useState(false);
  const [shotHistoryError, setShotHistoryError] = useState(null);

  // Drive API 상태
  const [driveData, setDriveData] = useState(null);
  const [isDriveLoading, setIsDriveLoading] = useState(false);
  const [driveError, setDriveError] = useState(null);

  // Alarm API 상태
  const [alarmData, setAlarmData] = useState(null);
  const [isAlarmLoading, setIsAlarmLoading] = useState(false);
  const [alarmError, setAlarmError] = useState(null);

  // SPC 상태
  const [selectedMeasurement, setSelectedMeasurement] = useState('중량');
  const spcMeasurements = [
    { id: 1, name: '중량', unit: 'g', lsl: 95, usl: 105, target: 100 },
    { id: 2, name: '치수A', unit: 'mm', lsl: 49.8, usl: 50.2, target: 50.0 },
    { id: 3, name: '치수B', unit: 'mm', lsl: 29.9, usl: 30.1, target: 30.0 },
    { id: 4, name: '두께', unit: 'mm', lsl: 2.45, usl: 2.55, target: 2.5 }
  ];

  // SPC API 상태
  const [selectedParameter, setSelectedParameter] = useState('cycle_time');
  const [spcData, setSpcData] = useState(null);
  const [isSpcLoading, setIsSpcLoading] = useState(false);
  const [spcError, setSpcError] = useState(null);

  // 사용자 정보 (로그인된 사용자) - useMemo로 캐싱하여 불필요한 리렌더링 방지
  const currentUser = React.useMemo(() => {
    const storedUser = getStoredUser();
    return storedUser ? { id: storedUser.id, name: storedUser.name } : null;
  }, []);

  // 위젯 데이터 API 상태
  const [widgetData, setWidgetData] = useState({});
  const widgetDataRef = useRef({}); // 최신 widgetData 참조용
  const [widgetLoading, setWidgetLoading] = useState({});
  const [isAllWidgetsLoading, setIsAllWidgetsLoading] = useState(false); // 전체 위젯 로딩 상태

  // 위젯 설정 로드 상태
  const [isLoadingWidgetConfig, setIsLoadingWidgetConfig] = useState(false);

  // 외부 클릭 감지 - dropdown 닫기
  useEffect(() => {
    const handleClickOutside = (event) => {
      const machineDropdown = event.target.closest('.machine-dropdown-wrapper');
      if (!machineDropdown && isDropdownOpen) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen]);

  // 사출기 목록 로딩
  useEffect(() => {
    setMachinesLoading(true);
    fetchEquipments()
      .then(equipments => {
        const equipmentList = equipments || [];
        setAllMachines(equipmentList);
        // machineData가 없고 설비 목록이 있으면 첫 번째 설비 선택
        if (!machine && equipmentList.length > 0) {
          setSelectedMachineId(equipmentList[0].id);
          setSelectedMachine(equipmentList[0].equipmentName || equipmentList[0].name);
        }
      })
      .catch(error => {
        console.error('Failed to load equipments:', error);
      })
      .finally(() => {
        setMachinesLoading(false);
      });
  }, [machine]);

  // URL에서 전달된 openTab 파라미터 처리
  useEffect(() => {
    if (openTab === 'alarm') {
      setActiveTab('알람');
    }
  }, [openTab]);

  const currentMachine = allMachines.find(m => m.id === selectedMachineId) || allMachines[0] || null;

  // Overview 데이터 초기 로딩 (모든 탭에서 기본 데이터로 필요)
  useEffect(() => {
    const equipmentId = selectedMachineId || machine?.id;

    if (equipmentId) {
      setIsOverviewLoading(true);
      fetchMachineOverview(equipmentId)
        .then(data => {
          if (data) {
            setOverviewApiData(data);
            setLastUpdated(new Date());
          }
        })
        .catch(error => {
          console.error('Failed to load overview data:', error);
        })
        .finally(() => {
          setIsOverviewLoading(false);
        });
    }
  }, [selectedMachineId, machine?.id]);

  // Overview 탭 선택 시 즉시 갱신
  useEffect(() => {
    const equipmentId = selectedMachineId || machine?.id;
    if (activeTab === 'Overview' && equipmentId) {
      // 탭 진입 시 즉시 로딩
      const loadOverviewData = async () => {
        try {
          setIsOverviewLoading(true);
          const data = await fetchMachineOverview(equipmentId);
          if (data) {
            setOverviewApiData(data);
            setLastUpdated(new Date());
          }
        } catch (error) {
          console.error('Overview 로드 실패:', error);
        } finally {
          setIsOverviewLoading(false);
        }
      };

      loadOverviewData();
    }
  }, [activeTab, selectedMachineId, machine?.id]);

  // Cycle 탭 선택 시 API 데이터 로딩
  useEffect(() => {
    const equipmentId = selectedMachineId || machine?.id;
    if (activeTab === 'Cycle' && equipmentId) {
      setIsCycleLoading(true);
      setCycleError(null);

      fetchMachineCycle(equipmentId)
        .then(data => {
          if (data) {
            setCycleData(data);
            setLastUpdated(new Date());
          }
        })
        .catch(error => {
          console.error('Failed to load cycle data:', error);
          setCycleError('사이클 데이터를 불러오는데 실패했습니다.');
        })
        .finally(() => {
          setIsCycleLoading(false);
        });
    }
  }, [activeTab, selectedMachineId, machine?.id]);

  // Temperature 탭 선택 시 API 데이터 로딩
  useEffect(() => {
    const equipmentId = selectedMachineId || machine?.id;
    if (activeTab === '온도' && equipmentId) {
      setIsTemperatureLoading(true);
      setTemperatureError(null);

      fetchMachineTemperature(equipmentId)
        .then(data => {
          if (data) {
            setTemperatureData(data);
            setLastUpdated(new Date());
          }
        })
        .catch(error => {
          console.error('Failed to load temperature data:', error);
          setTemperatureError('온도 데이터를 불러오는데 실패했습니다.');
        })
        .finally(() => {
          setIsTemperatureLoading(false);
        });
    }
  }, [activeTab, selectedMachineId, machine?.id]);

  // Drive 탭 선택 시 API 데이터 로딩
  useEffect(() => {
    const equipmentId = selectedMachineId || machine?.id;
    if (activeTab === '구동부' && equipmentId) {
      setIsDriveLoading(true);
      setDriveError(null);

      fetchMachineDrive(equipmentId)
        .then(data => {
          if (data) {
            setDriveData(data);
            setLastUpdated(new Date());
          }
        })
        .catch(error => {
          console.error('Failed to load drive data:', error);
          setDriveError('구동부 데이터를 불러오는데 실패했습니다.');
        })
        .finally(() => {
          setIsDriveLoading(false);
        });
    }
  }, [activeTab, selectedMachineId, machine?.id]);

  // Alarm 탭 선택 시 API 데이터 로딩
  useEffect(() => {
    const equipmentId = selectedMachineId || machine?.id;
    if (activeTab === '알람' && equipmentId) {
      setIsAlarmLoading(true);
      setAlarmError(null);

      fetchMachineAlarm(equipmentId)
        .then(data => {
          if (data) {
            setAlarmData(data);
            setLastUpdated(new Date());
          }
        })
        .catch(error => {
          console.error('Failed to load alarm data:', error);
          setAlarmError('알람 데이터를 불러오는데 실패했습니다.');
        })
        .finally(() => {
          setIsAlarmLoading(false);
        });
    }
  }, [activeTab, selectedMachineId, machine?.id]);

  // SPC 탭 선택 시 API 데이터 로딩
  useEffect(() => {
    const equipmentId = selectedMachineId || machine?.id;
    if (activeTab === 'SPC' && equipmentId) {
      setIsSpcLoading(true);
      setSpcError(null);

      fetchMachineSpc(equipmentId, selectedParameter)
        .then(data => {
          if (data) {
            setSpcData(data);
            setLastUpdated(new Date());
          }
        })
        .catch(error => {
          console.error('Failed to load SPC data:', error);
          setSpcError('SPC 데이터를 불러오는데 실패했습니다.');
        })
        .finally(() => {
          setIsSpcLoading(false);
        });
    }
  }, [activeTab, selectedMachineId, machine?.id, selectedParameter]);

  // 위젯 설정 로드 (페이지 로드 시 & 장비 변경 시)
  useEffect(() => {
    const loadWidgetConfig = async () => {
      if (!currentUser?.id) {
        return;
      }

      setIsLoadingWidgetConfig(true);

      try {
        const data = await fetchWidgetConfig(currentUser.id);

        if (data && data.length > 0) {
          // 저장된 위젯 설정을 dashboardWidgets 상태로 변환
          const widgets = data.map((config, index) => ({
            id: config.id,  // 데이터베이스 ID (사용자모듈 API 호출에 필요)
            type: config.widgetType,
            size: config.widgetSize,
            slot: config.slotNumber,
            config: config.customConfig
          }));
          setDashboardWidgets(widgets);
          // dashboardWidgets 변경으로 자동으로 데이터 fetch가 실행됨
        } else {
          // 저장된 설정이 없으면 빈 배열
          setDashboardWidgets([]);
        }
      } catch (error) {
        console.error('Failed to load widget config:', error);
      } finally {
        setIsLoadingWidgetConfig(false);
      }
    };

    loadWidgetConfig();
  }, [currentUser]);

  // Shot History 데이터 로딩 (양/불 탭 선택 시)
  useEffect(() => {
    const equipmentId = selectedMachineId || machine?.id;
    if (activeTab === '양/불' && equipmentId) {
      const loadShotHistory = async () => {
        setIsShotHistoryLoading(true);
        setShotHistoryError(null);

        try {
          const result = await fetchShotHistory(equipmentId, 100);

          if (!result.success || !result.data) {
            throw new Error('샷 이력 데이터 형식 오류');
          }

          // Mock API 데이터 직접 사용 (이미 올바른 형식)
          const shots = result.data.shots || result.data;
          const transformed = Array.isArray(shots) ? shots.map(shot => ({
            shotNum: shot.shotCount || shot.shotNum,
            shotCount: shot.shotCount || shot.shotNum,
            craftId: shot.craftId,
            shotTime: shot.shotTime,           // Pi 사출 감지 실시각 (백엔드 ISO 문자열)
            metering: shot.chargePosi1 || shot.metering,
            injectionTime: shot.injTime || shot.injectionTime,
            injection: shot.injStartPosi || shot.injection,
            cushion: shot.injEndPosi || shot.cushion,
            temperature: shot.temp1Current || shot.temperature,
            temp1Current: shot.temp1Current || shot.temperature,
            speed: shot.injSpeed1 || shot.speed,
            injSpeed1: shot.injSpeed1 || shot.speed,
            injPress1: shot.injPress1,
            qualityStatus: shot.qualityStatus  // 'GOOD', 'BAD', null/undefined
          })) : [];

          setShotHistoryData(transformed);

          // 최신 샷 자동 선택 (첫 번째 항목)
          if (transformed.length > 0) {
            setSelectedShotNumber(transformed[0].shotNum);
          }
        } catch (error) {
          console.error('Failed to load shot history:', error);
          setShotHistoryError('샷 이력 데이터를 불러오는데 실패했습니다.');
        } finally {
          setIsShotHistoryLoading(false);
        }
      };

      loadShotHistory();
    }
  }, [activeTab, selectedMachineId, machine?.id]);

  // 위젯 설정 저장 (위젯 변경 시 자동 저장 - 1초 debounce)
  useEffect(() => {
    // 로딩 중이거나 필수 정보가 없으면 저장 안 함
    if (!currentUser?.id || isLoadingWidgetConfig) {
      return;
    }

    const saveWidgetSettings = async () => {
      try {
        const result = await saveWidgetConfig(currentUser.id, dashboardWidgets);

        if (result) {
          // 새로 추가된 사용자모듈 위젯이 있는지 확인 (ID가 없었다가 생긴 경우)
          const hasNewCustomWidget = dashboardWidgets.some(widget =>
            widget.type === '사용자모듈' && !widget.id
          );

          // ID가 변경된 위젯이 있는지 확인
          const hasIdChanged = result.some((savedConfig) => {
            const existingWidget = dashboardWidgets.find(w => w.slot === savedConfig.slotNumber);
            return existingWidget && existingWidget.id !== savedConfig.id;
          });

          // ID가 변경된 경우에만 상태 업데이트
          if (hasIdChanged) {
            // 서버에서 반환한 savedConfigs로 위젯 상태 업데이트
            const updatedWidgets = result.map((savedConfig) => {
              // 기존 위젯에서 slot이 같은 것을 찾아 병합
              const existingWidget = dashboardWidgets.find(w => w.slot === savedConfig.slotNumber);
              return {
                id: savedConfig.id,  // 데이터베이스 ID
                type: savedConfig.widgetType,
                size: savedConfig.widgetSize,
                slot: savedConfig.slotNumber,
                config: savedConfig.customConfig || existingWidget?.config,
                _oldId: existingWidget?.id  // 기존 ID 저장 (widgetData 매핑용)
              };
            });

            // 🔧 widgetData의 키도 새 ID로 매핑
            setWidgetData(prevData => {
              const newData = { ...prevData };
              updatedWidgets.forEach(widget => {
                if (widget.type === '사용자모듈' && widget._oldId && widget._oldId !== widget.id) {
                  const oldKey = `사용자모듈_${widget._oldId}`;
                  const newKey = `사용자모듈_${widget.id}`;
                  if (newData[oldKey] !== undefined) {
                    newData[newKey] = newData[oldKey];
                    delete newData[oldKey];
                  }
                }
              });
              return newData;
            });

            // 로딩 플래그를 일시적으로 true로 설정하여 무한 루프 방지
            setIsLoadingWidgetConfig(true);
            setDashboardWidgets(updatedWidgets.map(({ _oldId, ...widget }) => widget)); // _oldId 제거

            // 다음 tick에서 로딩 플래그 해제
            setTimeout(() => {
              setIsLoadingWidgetConfig(false);
            }, 100);
            // dashboardWidgets 변경으로 자동으로 데이터 fetch가 실행됨
          }
        }
      } catch (error) {
        console.error('Failed to save widget config:', error);
      }
    };

    // 위젯 변경 후 1초 뒤 자동 저장 (debounce)
    const timer = setTimeout(saveWidgetSettings, 1000);
    return () => clearTimeout(timer);
  }, [dashboardWidgets, selectedMachineId, machine?.id, currentUser]);

  // dashboardWidgets를 ref에 동기화
  useEffect(() => {
    dashboardWidgetsRef.current = dashboardWidgets;
  }, [dashboardWidgets]);

  // 위젯 데이터 fetch 함수 (공통) - useCallback으로 정의하여 즉시 사용 가능
  const fetchAllWidgetData = useCallback(async () => {
    const equipmentId = selectedMachineId || machine?.id;
    const widgets = dashboardWidgetsRef.current; // ref에서 최신 값 참조

    // 이미 fetch 중이면 중복 실행 방지
    if (isFetchingWidgetDataRef.current) {
      return;
    }

    if (!equipmentId || widgets.length === 0) {
      return;
    }

    // 중복 실행 방지 플래그 설정
    isFetchingWidgetDataRef.current = true;

    // 전체 로딩 시작
    setIsAllWidgetsLoading(true);

    // 모든 데이터를 임시로 저장할 객체
    const newWidgetData = {};
    const newWidgetLoading = {};

    // 기본 위젯: 타입별로 중복 제거
    const basicWidgets = widgets.filter(w => w.type !== '사용자모듈');
    const uniqueTypes = [...new Set(basicWidgets.map(w => w.type))];

    // 사용자모듈 위젯
    const customWidgets = widgets.filter(w => w.type === '사용자모듈');

    // 초기 로드 시에만 로딩 상태 설정 (데이터가 없는 위젯만)
    // 이미 데이터가 있는 경우에는 로딩 표시 없이 백그라운드에서 업데이트
    const currentData = widgetDataRef.current;
    setWidgetLoading(prev => {
      const updated = { ...prev };
      uniqueTypes.forEach(type => {
        // 이미 데이터가 있으면 로딩 상태 변경하지 않음
        if (!currentData[type]) {
          updated[type] = true;
        }
      });
      customWidgets.forEach(widget => {
        if (widget.id) {
          const customKey = `사용자모듈_${widget.id}`;
          if (!currentData[customKey]) {
            updated[customKey] = true;
          }
        }
      });
      return updated;
    });

    try {
      // 기본 위젯 데이터 fetch (Promise.all로 병렬 처리)
      const basicFetchPromises = uniqueTypes.map(async (type) => {

        try {
          const data = await fetchMachineWidget(equipmentId, type);

          if (data) {
            // 알람이력 위젯은 전체 data 객체를 저장 (alarmHistory 배열 포함)
            if (type === '알람이력') {
              newWidgetData[type] = data;
            }
            // 기본정보 위젯은 basicInfo 객체를 저장
            else if (type === '기본정보') {
              newWidgetData[type] = data.basicInfo;
            }
            // 기본사출정보 위젯은 전체 data 객체를 저장 (injectionInfo 객체 포함)
            else if (type === '기본사출정보') {
              newWidgetData[type] = data;
            }
            else if (data.data) {
              newWidgetData[type] = data.data;
            }
          }
        } catch (error) {
          console.error(`Failed to fetch ${type} widget data:`, error);
        } finally {
          newWidgetLoading[type] = false;
        }
      });

      // 사용자모듈 위젯: 각각 개별적으로 fetch (Promise.all로 병렬 처리)
      const customFetchPromises = customWidgets.map(async (widget) => {
        if (!widget.id) {
          console.error('사용자모듈 위젯에 ID가 없습니다:', widget);
          return;
        }

        const customKey = `사용자모듈_${widget.id}`;

        try {
          const data = await fetchCustomWidget(equipmentId, widget.id);

          if (data && data.data) {
            newWidgetData[customKey] = data.data;
          }
        } catch (error) {
          console.error(`Failed to fetch custom widget ${widget.id} data:`, error);
        } finally {
          newWidgetLoading[customKey] = false;
        }
      });

      // 모든 fetch가 완료될 때까지 대기
      await Promise.all([...basicFetchPromises, ...customFetchPromises]);

      // 모든 데이터를 한 번에 업데이트 (기존 데이터와 병합)
      setWidgetData(prev => ({ ...prev, ...newWidgetData }));
      setWidgetLoading(prev => ({ ...prev, ...newWidgetLoading }));
    } finally {
      // 전체 로딩 종료 및 플래그 해제
      setIsAllWidgetsLoading(false);
      isFetchingWidgetDataRef.current = false;
    }
  }, [selectedMachineId, machine?.id]);

  // fetchAllWidgetData를 ref에 저장 (interval에서 최신 함수 참조용)
  useEffect(() => {
    fetchAllWidgetDataRef.current = fetchAllWidgetData;
  }, [fetchAllWidgetData]);

  // widgetData가 변경될 때마다 ref 업데이트
  useEffect(() => {
    widgetDataRef.current = widgetData;
  }, [widgetData]);

  // 대시보드 탭 진입 시 데이터 fetch
  useEffect(() => {
    if (activeTab === '대시보드' && dashboardWidgets.length > 0 && !isLoadingWidgetConfig) {
      // 탭 진입 시 항상 갱신
      if (fetchAllWidgetDataRef.current) {
        fetchAllWidgetDataRef.current();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isLoadingWidgetConfig]);

  // 통합 자동 갱신 콜백 함수
  const handleAutoRefresh = useCallback(async () => {
    const currentTab = activeTabRef.current;
    const equipmentId = selectedMachineIdRef.current || machine?.id;

    // 데이터홀드 중이면 Cycle 탭에서는 자동 갱신 스킵
    if (currentTab === 'Cycle' && dataHoldRef.current) {
      return;
    }

    setRefreshingUI(true);

    try {
      switch (currentTab) {
        case '대시보드':
          // 위젯 데이터와 Overview 데이터(lastSyncTime 포함) 동시 갱신
          const overviewPromise = equipmentId ? fetchMachineOverview(equipmentId) : Promise.resolve(null);
          const widgetPromise = fetchAllWidgetDataRef.current ? fetchAllWidgetDataRef.current() : Promise.resolve();
          const [overviewData] = await Promise.all([overviewPromise, widgetPromise]);
          if (overviewData) {
            setOverviewApiData(overviewData);
          }
          break;
        case 'Overview':
          const ovData = await fetchMachineOverview(equipmentId);
          if (ovData) {
            setOverviewApiData(ovData);
          }
          break;
        case 'Cycle':
          const cycData = await fetchMachineCycle(equipmentId);
          if (cycData) {
            setCycleData(cycData);
          }
          break;
        case '온도':
          const tempData = await fetchMachineTemperature(equipmentId);
          if (tempData) {
            setTemperatureData(tempData);
          }
          break;
        case '구동부':
          const drvData = await fetchMachineDrive(equipmentId);
          if (drvData) {
            setDriveData(drvData);
          }
          break;
        case '알람':
          const almData = await fetchMachineAlarm(equipmentId);
          if (almData) {
            setAlarmData(almData);
          }
          break;
        case 'SPC':
          const sData = await fetchMachineSpc(equipmentId, selectedParameter);
          if (sData) {
            setSpcData(sData);
          }
          break;
        default:
          break;
      }
      setLastUpdated(new Date());
    } catch (error) {
      console.error('[자동 갱신] 실패:', error);
    }

    setTimeout(() => {
      setRefreshingUI(false);
    }, 1500);
  }, [machine?.id, selectedParameter, setRefreshingUI]);

  // useAutoRefresh 훅 사용 (30초 쿨다운 적용)
  useAutoRefresh(handleAutoRefresh);

  // 위젯 타입별 엔드포인트 매핑
  const getWidgetEndpoint = (type) => {
    const endpointMap = {
      '온도': 'temperature',
      '압력': 'pressure',
      '에너지': 'energy',
      '기본정보': 'basic-info',
      '알람이력': 'alarm-history',
      '기본사출정보': 'injection-info',
      '사용자모듈': 'custom'
    };
    return endpointMap[type];
  };

  // Cycle 프로파일 차트 데이터 생성 함수 (0~1 단계)
  const generateCycleData = () => {
    return Array.from({ length: 100 }, (_, i) => ({
      step: i / 100,
      pressure: 50 + Math.random() * 50,  // 압력 (사출)
      speed: 30 + Math.random() * 40,     // 속도 (사출)
      holding: 40 + Math.random() * 40,   // 보압
      time: 20 + Math.random() * 30,      // 시간 (보압)
      backPressure: 30 + Math.random() * 50,  // 배압 (계량)
      cushion: 25 + Math.random() * 35,   // 쿠션위치 (계량)
      value: 50 + Math.random() * 50
    }));
  };

  // 사이클 타임 트렌드용 샷 번호 데이터
  const generateShotCountData = () => {
    return Array.from({ length: 50 }, (_, i) => ({
      shotNumber: i + 1,
      cycleTime: 40 + Math.random() * 10,      // 사이클타임
      injectionTime: 2 + Math.random() * 1,    // 사출시간
      holdingTime: 3 + Math.random() * 1,      // 보압시간
      meteringTime: 4 + Math.random() * 1,     // 계량시간
      coolingTime: 25 + Math.random() * 5      // 냉각시간
    }));
  };

  // 온도 트렌드용 샷 번호 데이터
  const generateTempTrendData = () => {
    const setTemp = {
      zone1: 250,
      zone2: 240,
      zone3: 230,
      zone4: 220,
      zone5: 210
    };

    return Array.from({ length: 50 }, (_, i) => ({
      shotNumber: i + 1,
      zone1: setTemp.zone1 + (Math.random() - 0.5) * 10,  // Zone1 현재온도
      zone2: setTemp.zone2 + (Math.random() - 0.5) * 10,  // Zone2 현재온도
      zone3: setTemp.zone3 + (Math.random() - 0.5) * 10,  // Zone3 현재온도
      zone4: setTemp.zone4 + (Math.random() - 0.5) * 10,  // Zone4 현재온도
      zone5: setTemp.zone5 + (Math.random() - 0.5) * 10,  // Zone5 현재온도
      setTemp1: setTemp.zone1,  // Zone1 설정온도
      setTemp2: setTemp.zone2,  // Zone2 설정온도
      setTemp3: setTemp.zone3,  // Zone3 설정온도
      setTemp4: setTemp.zone4,  // Zone4 설정온도
      setTemp5: setTemp.zone5   // Zone5 설정온도
    }));
  };

  // Overview 데이터
  const overviewData = {
    basicInfo: [
      { label: '제품명', value: 'AUTO-PART-A01', unit: '' },
      { label: '샷카운트', value: '203', unit: '회' },
      { label: '사이클타임', value: '13.40', unit: 's' },
      { label: '캐비티수', value: '4', unit: '개' }
    ],
    injection: [
      { label: '최대사출압력', value: '145.2', unit: 'MPa' },
      { label: '사출시작위치', value: '25.5', unit: 'mm' },
      { label: '사출전환위치', value: '12.8', unit: 'mm' },
      { label: '사출시간', value: '2.35', unit: 's' },
      { label: '사출속도', value: '85.0', unit: 'mm/s' },
      { label: '석백시간', value: '0.5', unit: 's' },
      { label: '석백속도', value: '15.0', unit: 'mm/s' }
    ],
    holding: [
      { label: '보압전환모드', value: '위치', unit: '' },
      { label: '보압전환입력', value: '8.5', unit: 'mm' },
      { label: '보압1단계압력', value: '95.0', unit: 'MPa' },
      { label: '보압2단계압력', value: '75.0', unit: 'MPa' },
      { label: '보압1단계시간', value: '3.2', unit: 's' },
      { label: '보압2단계시간', value: '2.8', unit: 's' }
    ],
    metering: [
      { label: '계량위치', value: '48.5', unit: 'mm' },
      { label: '계량속도', value: '35.0', unit: 'rpm' },
      { label: '계량압력', value: '12.5', unit: 'MPa' },
      { label: '계량시간', value: '4.2', unit: 's' }
    ],
    mold: [
      { label: '금형두께', value: '285', unit: 'mm' },
      { label: '형개위치', value: '350', unit: 'mm' },
      { label: '형폐위치', value: '0', unit: 'mm' },
      { label: '형개시간', value: '2.1', unit: 's' },
      { label: '형폐시간', value: '1.8', unit: 's' },
      { label: '금형온도1', value: '65', unit: '℃' },
      { label: '금형온도2', value: '68', unit: '℃' }
    ],
    ejector: [
      { label: '전진위치', value: '120', unit: 'mm' },
      { label: '후퇴위치', value: '0', unit: 'mm' },
      { label: '전진속도', value: '45', unit: 'mm/s' },
      { label: '후퇴속도', value: '35', unit: 'mm/s' }
    ],
    heater: [
      { label: '존1온도', value: '220', unit: '℃' },
      { label: '존2온도', value: '230', unit: '℃' },
      { label: '존3온도', value: '240', unit: '℃' },
      { label: '존4온도', value: '245', unit: '℃' },
      { label: '노즐온도', value: '250', unit: '℃' }
    ]
  };

  const tabs = [
    { name: '대시보드', icon: 'dashboard', color: '#60A5FA' },
    { name: 'Overview', icon: 'analytics', color: '#22C55E' },
    { name: 'Cycle', icon: 'autorenew', color: '#A78BFA' },
    { name: '온도', icon: 'thermostat', color: '#F59E0B' },
    { name: '구동부', icon: 'settings', color: '#8B5CF6' },
    { name: '알람', icon: 'notifications', color: '#EF4444' },
    { name: 'SPC', icon: 'assessment', color: '#06B6D4' },
    { name: '양/불', icon: 'check_circle', color: '#10B981' },
    { name: '감시1', icon: 'visibility', color: '#F472B6' },
    { name: '감시2', icon: 'visibility', color: '#FB923C' }
  ];

  // 커스텀 위젯 미리보기 렌더링
  const renderCustomPreview = () => {
    const previewData = Array.from({ length: 20 }, (_, i) => ({
      shotNumber: i + 1,
      pressure: 80 + Math.random() * 20,
      temperature: 230 + Math.random() * 20,
      speed: 50 + Math.random() * 30,
      position: 100 + Math.random() * 50
    }));

    if (customWidgetConfig.chartType === 'line') {
      return (
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={previewData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis dataKey={customWidgetConfig.xAxis} stroke="#9CA3AF" fontSize={10} />
            <YAxis stroke="#9CA3AF" fontSize={10} />
            <Tooltip
              contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', fontSize: '12px' }}
              labelStyle={{ color: '#E5E7EB', fontSize: '13px' }}
            />
            <Line type="monotone" dataKey={customWidgetConfig.yAxis} stroke="#3B82F6" strokeWidth={1} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      );
    } else if (customWidgetConfig.chartType === 'bar') {
      return (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={previewData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis dataKey={customWidgetConfig.xAxis} stroke="#9CA3AF" fontSize={10} />
            <YAxis stroke="#9CA3AF" fontSize={10} />
            <Tooltip
              contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', fontSize: '12px' }}
              labelStyle={{ color: '#E5E7EB', fontSize: '13px' }}
            />
            <Bar dataKey={customWidgetConfig.yAxis} fill="#8B5CF6" />
          </BarChart>
        </ResponsiveContainer>
      );
    } else if (customWidgetConfig.chartType === 'area') {
      return (
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={previewData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis dataKey={customWidgetConfig.xAxis} stroke="#9CA3AF" fontSize={10} />
            <YAxis stroke="#9CA3AF" fontSize={10} />
            <Tooltip
              contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', fontSize: '12px' }}
              labelStyle={{ color: '#E5E7EB', fontSize: '13px' }}
            />
            <Area type="monotone" dataKey={customWidgetConfig.yAxis} stroke="#10B981" fill="#10B981" fillOpacity={0.3} />
          </AreaChart>
        </ResponsiveContainer>
      );
    }
  };

  // 위젯 렌더링 함수
  const renderWidget = (widget) => {
    // 기본 위젯용 데이터 (사용자모듈 제외)
    const data = widgetData[widget.type] || [];
    const loading = widgetLoading[widget.type];

    // 사용자모듈은 별도 키로 로딩 상태 관리하므로 case 내부에서 처리
    if (widget.type !== '사용자모듈') {
      // 로딩 중
      if (loading) {
        return (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            color: '#9CA3AF'
          }}>
            로딩 중...
          </div>
        );
      }
    }

    // 데이터 없음 (사용자모듈과 생산 위젯은 제외)
    if (data.length === 0 && widget.type !== '사용자모듈' && widget.type !== '생산') {
      return (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          color: '#9CA3AF'
        }}>
          데이터 없음
        </div>
      );
    }

    switch(widget.type) {
      case '온도':
        // Y축 범위 계산 (모든 zone 값 고려)
        const tempValues = data.flatMap(d => [d.zone1, d.zone2, d.zone3, d.zone4, d.zone5].filter(v => v != null));
        const tempMin = tempValues.length > 0 ? Math.min(...tempValues) : 0;
        const tempMax = tempValues.length > 0 ? Math.max(...tempValues) : 100;
        const tempRange = tempMax - tempMin;
        const tempPadding = tempRange * 0.1; // 10% 여유

        return (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={[...data].reverse()} margin={{ top: 5, right: -20, left: -20, bottom: -5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis
                dataKey="shotNumber"
                stroke="#9CA3AF"
                tick={{ fill: '#9CA3AF', fontSize: 10 }}
                label={{ value: '샷 번호', position: 'insideBottom', offset: -5, fill: '#9CA3AF', fontSize: 11 }}
              />
              <YAxis
                stroke="#9CA3AF"
                tick={{ fill: '#9CA3AF', fontSize: 10 }}
                domain={[tempMin - tempPadding, tempMax + tempPadding]}
                tickFormatter={(value) => value.toFixed(1)}
                label={{ value: '온도(°C)', angle: -90, position: 'center', style: { textAnchor: 'middle' }, fill: '#9CA3AF', fontSize: 11 }}
              />
              <Tooltip
                contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', fontSize: '12px' }}
                labelStyle={{ color: '#E5E7EB', fontSize: '13px' }}
              />
              <Legend
                wrapperStyle={{
                  position: 'absolute',
                  top: '5px',
                  right: '30px',
                  fontSize: '10px',
                  width: 'auto'
                }}
                iconType="line"
                iconSize={16}
                layout="horizontal"
              />
              <Line type="monotone" dataKey="zone1" stroke="#EF4444" strokeWidth={1} dot={false} name="Zone 1" isAnimationActive={true} animationDuration={300} animationEasing="ease-out" />
              <Line type="monotone" dataKey="zone2" stroke="#F59E0B" strokeWidth={1} dot={false} name="Zone 2" isAnimationActive={true} animationDuration={300} animationEasing="ease-out" />
              <Line type="monotone" dataKey="zone3" stroke="#22C55E" strokeWidth={1} dot={false} name="Zone 3" isAnimationActive={true} animationDuration={300} animationEasing="ease-out" />
              <Line type="monotone" dataKey="zone4" stroke="#3B82F6" strokeWidth={1} dot={false} name="Zone 4" isAnimationActive={true} animationDuration={300} animationEasing="ease-out" />
              <Line type="monotone" dataKey="zone5" stroke="#8B5CF6" strokeWidth={1} dot={false} name="Zone 5" isAnimationActive={true} animationDuration={300} animationEasing="ease-out" />
            </LineChart>
          </ResponsiveContainer>
        );
      case '압력':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={[...data].reverse()} margin={{ top: 5, right: -20, left: -20, bottom: -5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis
                dataKey="shotNumber"
                stroke="#9CA3AF"
                tick={{ fill: '#9CA3AF', fontSize: 10 }}
                label={{ value: '샷 번호', position: 'insideBottom', offset: -5, fill: '#9CA3AF', fontSize: 11 }}
              />
              <YAxis
                stroke="#9CA3AF"
                tick={{ fill: '#9CA3AF', fontSize: 10 }}
                label={{ value: '압력(MPa)', angle: -90, position: 'center', style: { textAnchor: 'middle' }, fill: '#9CA3AF', fontSize: 11 }}
              />
              <Tooltip
                contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', fontSize: '12px' }}
                labelStyle={{ color: '#E5E7EB', fontSize: '13px' }}
              />
              <Legend
                wrapperStyle={{
                  position: 'absolute',
                  top: '5px',
                  right: '30px',
                  fontSize: '10px',
                  width: 'auto'
                }}
                iconType="line"
                iconSize={16}
                layout="horizontal"
              />
              <Line type="monotone" dataKey="injection" stroke="#3B82F6" strokeWidth={1} dot={false} name="사출압력" isAnimationActive={true} animationDuration={300} animationEasing="ease-out" />
              <Line type="monotone" dataKey="holding" stroke="#8B5CF6" strokeWidth={1} dot={false} name="보압" isAnimationActive={true} animationDuration={300} animationEasing="ease-out" />
              <Line type="monotone" dataKey="metering" stroke="#10B981" strokeWidth={1} dot={false} name="계량압력" isAnimationActive={true} animationDuration={300} animationEasing="ease-out" />
              <Line type="monotone" dataKey="backPressure" stroke="#F59E0B" strokeWidth={1} dot={false} name="배압" isAnimationActive={true} animationDuration={300} animationEasing="ease-out" />
            </LineChart>
          </ResponsiveContainer>
        );
      case '생산':
        return (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            color: '#9CA3AF'
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: '48px', marginBottom: '10px' }}>
              construction
            </span>
            <div>생산 위젯 준비 중</div>
          </div>
        );
      case '에너지':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={[...data].reverse()} margin={{ top: 5, right: -20, left: -20, bottom: -5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis
                dataKey="shotNumber"
                stroke="#9CA3AF"
                tick={{ fill: '#9CA3AF', fontSize: 10 }}
                label={{ value: '샷 번호', position: 'insideBottom', offset: -5, fill: '#9CA3AF', fontSize: 11 }}
              />
              <YAxis
                stroke="#9CA3AF"
                tick={{ fill: '#9CA3AF', fontSize: 10 }}
                label={{ value: '전력(kW)', angle: -90, position: 'center', style: { textAnchor: 'middle' }, fill: '#9CA3AF', fontSize: 11 }}
              />
              <Tooltip
                contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', fontSize: '12px' }}
                labelStyle={{ color: '#E5E7EB', fontSize: '13px' }}
              />
              <Legend
                wrapperStyle={{
                  position: 'absolute',
                  top: '5px',
                  right: '30px',
                  fontSize: '10px',
                  width: 'auto'
                }}
                iconType="line"
                iconSize={16}
                layout="horizontal"
              />
              <Area type="monotone" dataKey="power" stroke="#F59E0B" fill="#F59E0B" fillOpacity={0.3} name="전력(kW)" isAnimationActive={true} animationDuration={300} animationEasing="ease-out" />
            </AreaChart>
          </ResponsiveContainer>
        );
      case '기본정보':
        const basicInfo = widgetData['기본정보'] || {};
        return (
          <div className="info-list" style={{
            height: '100%',
            padding: '8px'
          }}>
            <div className="info-item">
              <span className="info-label">제품명</span>
              <span className="info-value">{basicInfo.productName || '-'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">샷카운트</span>
              <span className="info-value">
                {basicInfo.shotCount !== undefined ? basicInfo.shotCount.toLocaleString() : '-'}
                <span className="info-unit"> 회</span>
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">사이클타임</span>
              <span className="info-value">
                {basicInfo.cycleTime !== undefined ? basicInfo.cycleTime.toFixed(1) : '-'}
                <span className="info-unit"> s</span>
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">캐비티수</span>
              <span className="info-value">
                {basicInfo.cavityCount !== undefined ? basicInfo.cavityCount : '-'}
                <span className="info-unit"> 개</span>
              </span>
            </div>
          </div>
        );

      case '알람이력':
        const alarmHistory = widgetData['알람이력']?.alarmHistory || [];

        if (alarmHistory.length === 0) {
          return (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              color: '#9CA3AF'
            }}>
              알람이력 없음
            </div>
          );
        }

        // 심각도별 스타일 (CRITICAL, WARNING, INFO)
        const getSeverityStyleAlarm = (severity) => {
          switch (severity) {
            case 'CRITICAL':
              return {
                bg: 'rgba(239, 68, 68, 0.2)',
                border: 'rgba(239, 68, 68, 0.3)',
                text: '#f87171',
                icon: 'error'
              };
            case 'WARNING':
              return {
                bg: 'rgba(251, 191, 36, 0.2)',
                border: 'rgba(251, 191, 36, 0.3)',
                text: '#fbbf24',
                icon: 'warning'
              };
            case 'INFO':
              return {
                bg: 'rgba(59, 130, 246, 0.2)',
                border: 'rgba(59, 130, 246, 0.3)',
                text: '#60a5fa',
                icon: 'info'
              };
            default:
              return {
                bg: 'rgba(107, 114, 128, 0.2)',
                border: 'rgba(107, 114, 128, 0.3)',
                text: '#9ca3af',
                icon: 'circle'
              };
          }
        };

        // 시간 포맷팅
        const formatAlarmTime = (timeStr) => {
          const date = new Date(timeStr);
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          return `${month}/${day} ${hours}:${minutes}`;
        };

        return (
          <div style={{
            height: '100%',
            overflow: 'auto',
            padding: '8px',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}
          className="hide-scrollbar"
          >
            {alarmHistory.map((alarm, index) => {
              const severityStyle = getSeverityStyleAlarm(alarm.severity);
              return (
                <div
                  key={index}
                  style={{
                    display: 'block',
                    padding: '8px',
                    border: `1px solid ${severityStyle.border}`,
                    backgroundColor: severityStyle.bg,
                    transition: 'all 0.2s',
                    cursor: 'pointer',
                    textDecoration: 'none'
                  }}
                  onClick={() => navigate(`/alarm-analysis?machine=${encodeURIComponent(currentMachine?.name || machine.name)}&alarmId=${alarm.id || index}`)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = '0.8';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = '1';
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      flex: 1,
                      minWidth: 0
                    }}>
                      <div style={{
                        width: '20px',
                        height: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        color: severityStyle.text,
                        flexShrink: 0
                      }}>
                        <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>
                          {severityStyle.icon}
                        </span>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          flexWrap: 'wrap'
                        }}>
                          <span style={{
                            color: '#fff',
                            fontSize: '12px',
                            fontWeight: '500'
                          }}>
                            #{alarm.code}
                          </span>
                          {alarm.value && (
                            <span style={{
                              color: '#9ca3af',
                              fontSize: '11px'
                            }}>
                              {alarm.value}
                            </span>
                          )}
                          {alarm.status === 'active' && (
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              <div style={{
                                width: '6px',
                                height: '6px',
                                background: '#ef4444',
                                borderRadius: '50%',
                                animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                              }}></div>
                              <span style={{
                                color: '#f87171',
                                fontSize: '12px',
                                fontWeight: '500'
                              }}>
                                LIVE
                              </span>
                            </div>
                          )}
                        </div>
                        <div style={{
                          fontSize: '12px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          color: severityStyle.text
                        }}>
                          {alarm.message}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: '12px',
                    color: '#9ca3af',
                    marginTop: '4px',
                    marginLeft: '28px'
                  }}>
                    <span>발생: {formatAlarmTime(alarm.time)}</span>
                    <span style={{
                      color: alarm.status === 'active' ? '#f87171' : '#4ade80',
                      fontWeight: '500'
                    }}>
                      {alarm.status === 'active' ? '발생중' : '해결됨'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        );

      case '기본사출정보':
        const injectionInfo = widgetData['기본사출정보']?.injectionInfo;

        if (!injectionInfo) {
          return (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              color: '#9CA3AF'
            }}>
              데이터 없음
            </div>
          );
        }

        return (
          <div className="info-list" style={{
            height: '100%',
            padding: '8px',
            gridTemplateRows: 'repeat(4, 1fr)'
          }}>
            <div className="info-item">
              <span className="info-label">최대사출압력</span>
              <span className="info-value">
                {injectionInfo.maxPressure !== null && injectionInfo.maxPressure !== undefined
                  ? injectionInfo.maxPressure.toFixed(1)
                  : '-'}
                <span className="info-unit"> MPa</span>
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">사출시작위치</span>
              <span className="info-value">
                {injectionInfo.startPosition !== null && injectionInfo.startPosition !== undefined
                  ? injectionInfo.startPosition.toFixed(1)
                  : '-'}
                <span className="info-unit"> mm</span>
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">사출전환위치</span>
              <span className="info-value">
                {injectionInfo.switchPosition !== null && injectionInfo.switchPosition !== undefined
                  ? injectionInfo.switchPosition.toFixed(1)
                  : '-'}
                <span className="info-unit"> mm</span>
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">사출시간</span>
              <span className="info-value">
                {injectionInfo.time !== null && injectionInfo.time !== undefined
                  ? injectionInfo.time.toFixed(1)
                  : '-'}
                <span className="info-unit"> s</span>
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">사출속도</span>
              <span className="info-value">
                {injectionInfo.speed !== null && injectionInfo.speed !== undefined
                  ? injectionInfo.speed.toFixed(1)
                  : '-'}
                <span className="info-unit"> mm/s</span>
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">석백시간</span>
              <span className="info-value">
                {injectionInfo.suckbackTime !== null && injectionInfo.suckbackTime !== undefined
                  ? injectionInfo.suckbackTime.toFixed(1)
                  : '-'}
                <span className="info-unit"> s</span>
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">석백속도</span>
              <span className="info-value">
                {injectionInfo.suckbackSpeed !== null && injectionInfo.suckbackSpeed !== undefined
                  ? injectionInfo.suckbackSpeed.toFixed(1)
                  : '-'}
                <span className="info-unit"> mm/s</span>
              </span>
            </div>
          </div>
        );

      case '사용자모듈':
        // 사용자 정의 위젯 렌더링
        const customKey = `사용자모듈_${widget.id}`;
        const customData = widgetData[customKey] || [];
        const customLoading = widgetLoading[customKey];
        // 이 위젯의 데이터가 한번이라도 fetch되었는지 확인
        const hasCustomDataLoaded = widgetData.hasOwnProperty(customKey);

        if (!widget.config) {
          console.error('사용자모듈 위젯 설정 오류:', widget);
          return <div style={{ color: '#9CA3AF', textAlign: 'center', padding: '20px' }}>설정 오류</div>;
        }

        // 아직 데이터가 한번도 로드되지 않은 경우 (초기 상태)
        // → 로딩 중 또는 빈 상태 표시 (데이터 없음이 아님)
        if (!hasCustomDataLoaded) {
          return (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              color: '#9CA3AF'
            }}>
              {customLoading ? '로딩 중...' : ''}
            </div>
          );
        }

        // 데이터 로드 후 빈 배열인 경우
        if (customData.length === 0) {
          return (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              color: '#9CA3AF'
            }}>
              데이터 없음
            </div>
          );
        }

        // ⭐ 압력 차트인 경우: 3가지 압력 모두 표시
        if (widget.config.yAxis === 'pressure') {
          return (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={[...customData].reverse()} margin={{ top: 5, right: -20, left: -20, bottom: -5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis
                  dataKey={widget.config.xAxis || 'shotNumber'}
                  stroke="#9CA3AF"
                  tick={{ fill: '#9CA3AF', fontSize: 10 }}
                  label={{ value: '샷 번호', position: 'insideBottom', offset: -5, fill: '#9CA3AF', fontSize: 11 }}
                  interval={1}
                />
                <YAxis
                  stroke="#9CA3AF"
                  tick={{ fill: '#9CA3AF', fontSize: 10 }}
                  label={{ value: '압력(MPa)', angle: -90, position: 'center', style: { textAnchor: 'middle' }, fill: '#9CA3AF', fontSize: 11 }}
                />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', fontSize: '12px' }}
                  labelStyle={{ color: '#E5E7EB', fontSize: '13px' }}
                />
                <Legend
                  wrapperStyle={{
                    position: 'absolute',
                    top: '5px',
                    right: '30px',
                    fontSize: '10px',
                    width: 'auto'
                  }}
                  iconType="line"
                  iconSize={16}
                  layout="horizontal"
                />
                <Line type="monotone" dataKey="injection" stroke="#3B82F6" strokeWidth={1} dot={false} name="사출압력" isAnimationActive={true} animationDuration={300} animationEasing="ease-out" />
                <Line type="monotone" dataKey="holding" stroke="#8B5CF6" strokeWidth={1} dot={false} name="보압" isAnimationActive={true} animationDuration={300} animationEasing="ease-out" />
                <Line type="monotone" dataKey="metering" stroke="#10B981" strokeWidth={1} dot={false} name="계량압력" isAnimationActive={true} animationDuration={300} animationEasing="ease-out" />
                <Line type="monotone" dataKey="backPressure" stroke="#F59E0B" strokeWidth={1} dot={false} name="배압" isAnimationActive={true} animationDuration={300} animationEasing="ease-out" />
              </LineChart>
            </ResponsiveContainer>
          );
        }

        // ⭐ 온도 차트인 경우: 5개 zone 모두 표시
        if (widget.config.yAxis === 'temperature') {
          // 온도 데이터의 최소/최대값 계산
          const tempValues = customData.flatMap(item => [
            item.zone1, item.zone2, item.zone3, item.zone4, item.zone5
          ].filter(val => val !== undefined && val !== null));

          const minTemp = tempValues.length > 0 ? Math.min(...tempValues) : 0;
          const maxTemp = tempValues.length > 0 ? Math.max(...tempValues) : 300;

          // 여유 범위 추가 (최소값에서 -10, 최대값에서 +10)
          const yMin = Math.max(0, Math.floor(minTemp / 10) * 10 - 10);
          const yMax = Math.ceil(maxTemp / 10) * 10 + 10;

          return (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={[...customData].reverse()} margin={{ top: 5, right: -20, left: -20, bottom: -5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis
                  dataKey={widget.config.xAxis || 'shotNumber'}
                  stroke="#9CA3AF"
                  tick={{ fill: '#9CA3AF', fontSize: 10 }}
                  label={{ value: '샷 번호', position: 'insideBottom', offset: -5, fill: '#9CA3AF', fontSize: 11 }}
                />
                <YAxis
                  domain={[yMin, yMax]}
                  stroke="#9CA3AF"
                  tick={{ fill: '#9CA3AF', fontSize: 10 }}
                  label={{ value: '온도(°C)', angle: -90, position: 'center', style: { textAnchor: 'middle' }, fill: '#9CA3AF', fontSize: 11 }}
                />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', fontSize: '12px' }}
                  labelStyle={{ color: '#E5E7EB', fontSize: '13px' }}
                />
                <Legend
                  wrapperStyle={{
                    position: 'absolute',
                    top: '5px',
                    right: '30px',
                    fontSize: '10px',
                    width: 'auto'
                  }}
                  iconType="line"
                  iconSize={16}
                  layout="horizontal"
                />
                <Line type="monotone" dataKey="zone1" stroke="#EF4444" strokeWidth={1} dot={false} name="Zone 1" isAnimationActive={true} animationDuration={300} animationEasing="ease-out" />
                <Line type="monotone" dataKey="zone2" stroke="#F59E0B" strokeWidth={1} dot={false} name="Zone 2" isAnimationActive={true} animationDuration={300} animationEasing="ease-out" />
                <Line type="monotone" dataKey="zone3" stroke="#22C55E" strokeWidth={1} dot={false} name="Zone 3" isAnimationActive={true} animationDuration={300} animationEasing="ease-out" />
                <Line type="monotone" dataKey="zone4" stroke="#3B82F6" strokeWidth={1} dot={false} name="Zone 4" isAnimationActive={true} animationDuration={300} animationEasing="ease-out" />
                <Line type="monotone" dataKey="zone5" stroke="#8B5CF6" strokeWidth={1} dot={false} name="Zone 5" isAnimationActive={true} animationDuration={300} animationEasing="ease-out" />
              </LineChart>
            </ResponsiveContainer>
          );
        }

        // ⭐ 속도 차트인 경우
        if (widget.config.yAxis === 'speed') {
          return (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={[...customData].reverse()} margin={{ top: 5, right: -20, left: -20, bottom: -5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis
                  dataKey={widget.config.xAxis || 'shotNumber'}
                  stroke="#9CA3AF"
                  tick={{ fill: '#9CA3AF', fontSize: 10 }}
                  label={{ value: '샷 번호', position: 'insideBottom', offset: -5, fill: '#9CA3AF', fontSize: 11 }}
                />
                <YAxis
                  stroke="#9CA3AF"
                  tick={{ fill: '#9CA3AF', fontSize: 10 }}
                  label={{ value: '속도(mm/s)', angle: -90, position: 'center', style: { textAnchor: 'middle' }, fill: '#9CA3AF', fontSize: 11 }}
                />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', fontSize: '12px' }}
                  labelStyle={{ color: '#E5E7EB', fontSize: '13px' }}
                />
                <Legend
                  wrapperStyle={{
                    position: 'absolute',
                    top: '5px',
                    right: '30px',
                    fontSize: '10px',
                    width: 'auto'
                  }}
                  iconType="line"
                  iconSize={16}
                  layout="horizontal"
                />
                <Line type="monotone" dataKey="speed" stroke="#10B981" strokeWidth={1} dot={false} name="속도" isAnimationActive={true} animationDuration={300} animationEasing="ease-out" />
              </LineChart>
            </ResponsiveContainer>
          );
        }

        // ⭐ 위치 차트인 경우
        if (widget.config.yAxis === 'position') {
          return (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={[...customData].reverse()} margin={{ top: 5, right: -20, left: -20, bottom: -5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis
                  dataKey={widget.config.xAxis || 'shotNumber'}
                  stroke="#9CA3AF"
                  tick={{ fill: '#9CA3AF', fontSize: 10 }}
                  label={{ value: '샷 번호', position: 'insideBottom', offset: -5, fill: '#9CA3AF', fontSize: 11 }}
                />
                <YAxis
                  stroke="#9CA3AF"
                  tick={{ fill: '#9CA3AF', fontSize: 10 }}
                  label={{ value: '위치(mm)', angle: -90, position: 'center', style: { textAnchor: 'middle' }, fill: '#9CA3AF', fontSize: 11 }}
                />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', fontSize: '12px' }}
                  labelStyle={{ color: '#E5E7EB', fontSize: '13px' }}
                />
                <Legend
                  wrapperStyle={{
                    position: 'absolute',
                    top: '5px',
                    right: '30px',
                    fontSize: '10px',
                    width: 'auto'
                  }}
                  iconType="line"
                  iconSize={16}
                  layout="horizontal"
                />
                <Line type="monotone" dataKey="position" stroke="#F59E0B" strokeWidth={1} dot={false} name="위치" isAnimationActive={true} animationDuration={300} animationEasing="ease-out" />
              </LineChart>
            </ResponsiveContainer>
          );
        }

        if (widget.config.chartType === 'line') {
          return (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={[...customData].reverse()}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey={widget.config.xAxis} stroke="#9CA3AF" fontSize={10} />
                <YAxis stroke="#9CA3AF" fontSize={10} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', fontSize: '12px' }}
                  labelStyle={{ color: '#E5E7EB', fontSize: '13px' }}
                />
                <Line type="monotone" dataKey={widget.config.yAxis} stroke="#3B82F6" strokeWidth={1} dot={false} isAnimationActive={true} animationDuration={300} animationEasing="ease-out" />
              </LineChart>
            </ResponsiveContainer>
          );
        } else if (widget.config.chartType === 'bar') {
          return (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={[...customData].reverse()}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey={widget.config.xAxis} stroke="#9CA3AF" fontSize={10} />
                <YAxis stroke="#9CA3AF" fontSize={10} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', fontSize: '12px' }}
                  labelStyle={{ color: '#E5E7EB', fontSize: '13px' }}
                />
                <Bar dataKey={widget.config.yAxis} fill="#8B5CF6" isAnimationActive={true} animationDuration={300} animationEasing="ease-out" />
              </BarChart>
            </ResponsiveContainer>
          );
        } else if (widget.config.chartType === 'area') {
          return (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={[...customData].reverse()}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey={widget.config.xAxis} stroke="#9CA3AF" fontSize={10} />
                <YAxis stroke="#9CA3AF" fontSize={10} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', fontSize: '12px' }}
                  labelStyle={{ color: '#E5E7EB', fontSize: '13px' }}
                />
                <Area type="monotone" dataKey={widget.config.yAxis} stroke="#10B981" fill="#10B981" fillOpacity={0.3} isAnimationActive={true} animationDuration={300} animationEasing="ease-out" />
              </AreaChart>
            </ResponsiveContainer>
          );
        }
        return <div style={{ color: '#9CA3AF', fontSize: '14px' }}>위젯 내용</div>;
      default:
        return <div style={{ color: '#9CA3AF', fontSize: '14px' }}>위젯 내용</div>;
    }
  };

  // 로딩 중
  if (machinesLoading) {
    return (
      <MainLayout>
        <div className="machine-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <div style={{ textAlign: 'center', color: 'white' }}>
            <div style={{ fontSize: '18px' }}>데이터를 불러오는 중...</div>
          </div>
        </div>
      </MainLayout>
    );
  }

  // 설비가 없는 경우
  if (!machinesLoading && allMachines.length === 0) {
    return (
      <MainLayout>
        <div className="machine-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <div style={{ textAlign: 'center', color: 'white' }}>
            <div style={{ fontSize: '18px', marginBottom: '16px' }}>등록된 설비가 없습니다</div>
            <div style={{ fontSize: '14px', opacity: 0.7 }}>시스템 관리 메뉴에서 설비를 먼저 등록해주세요</div>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="machine-container">
        {/* 헤더 */}
        <div className="machine-header">
          <div className="machine-header-left">
            <div className="machine-header-info">
              <div className="machine-dropdown-wrapper">
                <button
                  className="machine-dropdown-trigger"
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                >
                  <span className="machine-title">{currentMachine?.equipmentName || currentMachine?.name || '설비 선택'}</span>
                  <span className="material-symbols-outlined dropdown-icon">
                    {isDropdownOpen ? 'expand_less' : 'expand_more'}
                  </span>
                </button>
                {isDropdownOpen && (
                  <div className="machine-dropdown-menu">
                    {allMachines.map((m) => (
                      <div
                        key={m.id}
                        className={`machine-dropdown-item ${selectedMachineId === m.id ? 'active' : ''}`}
                        onClick={() => {
                          setSelectedMachineId(m.id);
                          setSelectedMachine(m.equipmentName);
                          setIsDropdownOpen(false);
                        }}
                      >
                        <div className="dropdown-item-name">{m.equipmentName}</div>
                        <div className="dropdown-item-model">{m.manufacturer} · {m.model}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <p className="machine-subtitle">
                {currentMachine?.manufacturer} · {currentMachine?.model}
              </p>
            </div>
            <div className="header-divider"></div>
            <div className="factory-name">{currentMachine?.factoryName}</div>
          </div>
          <div className="machine-header-right">
            <div className="update-label-container">
              <span ref={refreshLabelRef} className="update-label">업데이트</span>
              <span ref={refreshIconRef} className="material-symbols-outlined refresh-icon">
                sync
              </span>
            </div>
            <div className="update-time">
              {isOverviewLoading ? '로딩 중...' : (
                (() => {
                  // 현재 탭에 따라 해당 API의 lastSyncTime 표시
                  let syncTime = null;
                  switch (activeTab) {
                    case 'Overview':
                      syncTime = overviewApiData?.lastSyncTime;
                      break;
                    case 'Cycle':
                      syncTime = cycleData?.lastSyncTime;
                      break;
                    case '온도':
                      syncTime = temperatureData?.lastSyncTime;
                      break;
                    case '구동부':
                      syncTime = driveData?.lastSyncTime;
                      break;
                    case '알람':
                      syncTime = alarmData?.lastSyncTime;
                      break;
                    case 'SPC':
                      syncTime = spcData?.lastSyncTime;
                      break;
                    default:
                      syncTime = overviewApiData?.lastSyncTime;
                  }
                  return syncTime
                    ? new Date(syncTime).toLocaleString('ko-KR')
                    : lastUpdated.toLocaleString('ko-KR');
                })()
              )}
            </div>
          </div>
        </div>

        {/* 탭 메뉴 */}
        <div className="machine-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.name}
              className={`tab-item ${activeTab === tab.name ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.name)}
              style={
                activeTab !== tab.name &&
                ['Overview', 'Cycle', '온도', '구동부', '알람', 'SPC', '감시1', '감시2'].includes(tab.name)
                  ? { backgroundColor: 'rgb(64, 41, 41)' }
                  : {}
              }
            >
              <span
                className="material-symbols-outlined tab-icon"
                style={{ color: tab.color }}
              >
                {tab.icon}
              </span>
              {tab.name}
            </button>
          ))}
        </div>

        {/* 컨텐츠 영역 */}
        <div className="machine-content">
          {activeTab === '대시보드' && (
            <>
              <div className="dashboard-grid">
                {Array.from({ length: 12 }).map((_, index) => {
                  const slotNumber = index + 1;
                  const widget = dashboardWidgets.find(w => w.slot === slotNumber);

                  // 다른 위젯에 의해 차지된 슬롯인지 확인
                  const isOccupied = dashboardWidgets.some(w => {
                    if (w.slot === slotNumber) return false; // 자신은 제외

                    const occupiedSlots = [];
                    const row = Math.floor((w.slot - 1) / 4);
                    const col = (w.slot - 1) % 4;

                    if (w.size === '1x1') {
                      occupiedSlots.push(w.slot);
                    } else if (w.size === '2x1') {
                      occupiedSlots.push(w.slot, w.slot + 1);
                    } else if (w.size === '1x2') {
                      occupiedSlots.push(w.slot, w.slot + 4);
                    } else if (w.size === '2x2') {
                      occupiedSlots.push(w.slot, w.slot + 1, w.slot + 4, w.slot + 5);
                    }

                    return occupiedSlots.includes(slotNumber);
                  });

                  if (isOccupied && !widget) return null;

                  if (widget) {
                    const getGridClass = (size) => {
                      switch(size) {
                        case '1x1': return 'dashboard-widget-1x1';
                        case '2x1': return 'dashboard-widget-2x1';
                        case '1x2': return 'dashboard-widget-1x2';
                        case '2x2': return 'dashboard-widget-2x2';
                        default: return 'dashboard-widget-1x1';
                      }
                    };

                    // 위젯 제목에 단위 추가
                    const getWidgetTitle = (widget) => {
                      if (widget.type === '사용자모듈' && widget.config?.title) {
                        return widget.config.title;
                      }
                      const unitMap = {
                        '온도': '온도(°C)',
                        '압력': '압력(MPa)',
                        '에너지': '에너지(kW)'
                      };
                      return unitMap[widget.type] || widget.type;
                    };

                    return (
                      <div key={index} className={`dashboard-card ${getGridClass(widget.size)}`}>
                        <div className="widget-container">
                          <div className="widget-header">
                            <h3>{getWidgetTitle(widget)}</h3>
                            <button
                              className="widget-remove-btn"
                              onClick={() => {
                                setDashboardWidgets(dashboardWidgets.filter(w => w.id !== widget.id));
                              }}
                            >
                              <span className="material-symbols-outlined">close</span>
                            </button>
                          </div>
                          <div className="widget-content">
                            {renderWidget(widget)}
                          </div>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={index} className="dashboard-card">
                      <div className="card-placeholder">
                        <button
                          className="add-widget-btn"
                          onClick={() => {
                            setSelectedWidgetSlot(slotNumber);
                            setIsWidgetModalOpen(true);
                          }}
                        >
                          <span className="material-symbols-outlined">add</span>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* 위젯 추가 모달 */}
              {isWidgetModalOpen && (
                <div className="widget-modal-overlay" onClick={() => setIsWidgetModalOpen(false)}>
                  <div className="widget-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="widget-modal-header">
                      <h2>대시보드 위젯 추가</h2>
                      <button className="modal-close-btn" onClick={() => setIsWidgetModalOpen(false)}>
                        <span className="material-symbols-outlined">close</span>
                      </button>
                    </div>
                    <div className="widget-modal-body">
                      {!showSizeSelector ? (
                      <div className="widget-list">
                        <div
                          className="widget-item"
                          onClick={() => {
                            setSelectedWidgetType('온도');
                            setShowSizeSelector(true);
                          }}
                        >
                          <div className="widget-icon">
                            <span className="material-symbols-outlined">thermostat</span>
                          </div>
                          <div className="widget-info">
                            <h3>온도</h3>
                            <p>Zone별 온도 현황 및 추이</p>
                          </div>
                        </div>
                        <div
                          className="widget-item"
                          onClick={() => {
                            setSelectedWidgetType('압력');
                            setShowSizeSelector(true);
                          }}
                        >
                          <div className="widget-icon">
                            <span className="material-symbols-outlined">compress</span>
                          </div>
                          <div className="widget-info">
                            <h3>압력</h3>
                            <p>사출/보압 압력 모니터링</p>
                          </div>
                        </div>
                        <div
                          className="widget-item"
                          onClick={() => {
                            setSelectedWidgetType('에너지');
                            setShowSizeSelector(true);
                          }}
                        >
                          <div className="widget-icon">
                            <span className="material-symbols-outlined">bolt</span>
                          </div>
                          <div className="widget-info">
                            <h3>에너지</h3>
                            <p>전력 소비량 및 효율</p>
                          </div>
                        </div>
                        <div
                          className="widget-item"
                          onClick={() => {
                            setSelectedWidgetType('기본정보');
                            setShowSizeSelector(true);
                          }}
                        >
                          <div className="widget-icon">
                            <span className="material-symbols-outlined">info</span>
                          </div>
                          <div className="widget-info">
                            <h3>기본정보</h3>
                            <p>제품명, 샷카운트, 사이클타임, 캐비티수</p>
                          </div>
                        </div>
                        <div
                          className="widget-item"
                          onClick={() => {
                            setSelectedWidgetType('알람이력');
                            setShowSizeSelector(true);
                          }}
                        >
                          <div className="widget-icon">
                            <span className="material-symbols-outlined">notification_important</span>
                          </div>
                          <div className="widget-info">
                            <h3>알람이력</h3>
                            <p>최근 알람 발생 이력</p>
                          </div>
                        </div>
                        <div
                          className="widget-item"
                          onClick={() => {
                            setSelectedWidgetType('기본사출정보');
                            setShowSizeSelector(true);
                          }}
                        >
                          <div className="widget-icon">
                            <span className="material-symbols-outlined">straighten</span>
                          </div>
                          <div className="widget-info">
                            <h3>기본사출정보</h3>
                            <p>사출 설정값 및 파라미터</p>
                          </div>
                        </div>
                        <div
                          className="widget-item"
                          onClick={() => {
                            setSelectedWidgetType('사용자모듈');
                            setShowCustomWidgetBuilder(true);
                            setCustomWidgetConfig({
                              size: '1x1',
                              title: '',
                              chartType: 'line',
                              xAxis: 'shotNumber',
                              yAxis: 'pressure'
                            });
                          }}
                        >
                          <div className="widget-icon">
                            <span className="material-symbols-outlined">dashboard_customize</span>
                          </div>
                          <div className="widget-info">
                            <h3>사용자 모듈</h3>
                            <p>커스텀 위젯 추가</p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="size-selector">
                        <h3>위젯 크기 선택: {selectedWidgetType}</h3>
                        <div className="size-options">
                          <div
                            className="size-option"
                            onClick={() => {
                              setDashboardWidgets([...dashboardWidgets, {
                                id: Date.now(),
                                type: selectedWidgetType,
                                size: '1x1',
                                slot: selectedWidgetSlot
                              }]);
                              setIsWidgetModalOpen(false);
                              setShowSizeSelector(false);
                              setSelectedWidgetType(null);
                            }}
                          >
                            <div className="size-preview size-1x1"></div>
                            <span>1×1 (기본)</span>
                          </div>
                          <div
                            className="size-option"
                            onClick={() => {
                              setDashboardWidgets([...dashboardWidgets, {
                                id: Date.now(),
                                type: selectedWidgetType,
                                size: '2x1',
                                slot: selectedWidgetSlot
                              }]);
                              setIsWidgetModalOpen(false);
                              setShowSizeSelector(false);
                              setSelectedWidgetType(null);
                            }}
                          >
                            <div className="size-preview size-2x1"></div>
                            <span>2×1 (가로)</span>
                          </div>
                          <div
                            className="size-option"
                            onClick={() => {
                              setDashboardWidgets([...dashboardWidgets, {
                                id: Date.now(),
                                type: selectedWidgetType,
                                size: '1x2',
                                slot: selectedWidgetSlot
                              }]);
                              setIsWidgetModalOpen(false);
                              setShowSizeSelector(false);
                              setSelectedWidgetType(null);
                            }}
                          >
                            <div className="size-preview size-1x2"></div>
                            <span>1×2 (세로)</span>
                          </div>
                          <div
                            className="size-option"
                            onClick={() => {
                              setDashboardWidgets([...dashboardWidgets, {
                                id: Date.now(),
                                type: selectedWidgetType,
                                size: '2x2',
                                slot: selectedWidgetSlot
                              }]);
                              setIsWidgetModalOpen(false);
                              setShowSizeSelector(false);
                              setSelectedWidgetType(null);
                            }}
                          >
                            <div className="size-preview size-2x2"></div>
                            <span>2×2 (크게)</span>
                          </div>
                        </div>
                        <button
                          className="back-btn"
                          onClick={() => {
                            setShowSizeSelector(false);
                            setSelectedWidgetType(null);
                          }}
                        >
                          <span className="material-symbols-outlined">arrow_back</span>
                          뒤로 가기
                        </button>
                      </div>
                    )}
                    </div>
                  </div>
                </div>
              )}

              {/* 사용자 모듈 빌더 */}
              {showCustomWidgetBuilder && (
                <div className="widget-modal-overlay" onClick={() => {
                  setShowCustomWidgetBuilder(false);
                  setSelectedWidgetType(null);
                }}>
                  <div className="custom-widget-builder" onClick={(e) => e.stopPropagation()}>
                    <div className="builder-header">
                      <h2>사용자 모듈 만들기</h2>
                      <button className="modal-close-btn" onClick={() => {
                        setShowCustomWidgetBuilder(false);
                        setSelectedWidgetType(null);
                      }}>
                        <span className="material-symbols-outlined">close</span>
                      </button>
                    </div>
                    <div className="builder-body">
                      {/* 좌측 설정 영역 */}
                      <div className="builder-settings">
                        <div className="setting-group">
                          <label className="setting-label">위젯 크기</label>
                          <div className="size-button-group">
                            {['1x1', '2x1', '1x2', '2x2'].map(size => (
                              <button
                                key={size}
                                className={`size-btn ${customWidgetConfig.size === size ? 'active' : ''}`}
                                onClick={() => setCustomWidgetConfig({...customWidgetConfig, size})}
                              >
                                {size === '1x1' && '1×1 (기본)'}
                                {size === '2x1' && '2×1 (가로)'}
                                {size === '1x2' && '1×2 (세로)'}
                                {size === '2x2' && '2×2 (크게)'}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="setting-group">
                          <label className="setting-label">위젯 제목</label>
                          <input
                            type="text"
                            className="setting-input"
                            placeholder="위젯 제목 입력"
                            value={customWidgetConfig.title}
                            onChange={(e) => setCustomWidgetConfig({...customWidgetConfig, title: e.target.value})}
                          />
                        </div>

                        <div className="setting-group">
                          <label className="setting-label">차트 타입</label>
                          <select
                            className="setting-select"
                            value={customWidgetConfig.chartType}
                            onChange={(e) => setCustomWidgetConfig({...customWidgetConfig, chartType: e.target.value})}
                          >
                            <option value="line">라인 차트</option>
                            <option value="bar">바 차트</option>
                            <option value="area">영역 차트</option>
                          </select>
                        </div>

                        <div className="setting-group">
                          <label className="setting-label">X축 데이터</label>
                          <select
                            className="setting-select"
                            value={customWidgetConfig.xAxis}
                            onChange={(e) => setCustomWidgetConfig({...customWidgetConfig, xAxis: e.target.value})}
                          >
                            <option value="shotNumber">샷 번호</option>
                            <option value="time">시간</option>
                          </select>
                        </div>

                        <div className="setting-group">
                          <label className="setting-label">Y축 데이터</label>
                          <select
                            className="setting-select"
                            value={customWidgetConfig.yAxis}
                            onChange={(e) => setCustomWidgetConfig({...customWidgetConfig, yAxis: e.target.value})}
                          >
                            <option value="pressure">압력 (MPa)</option>
                            <option value="temperature">온도 (℃)</option>
                            <option value="speed">속도 (mm/s)</option>
                            <option value="position">위치 (mm)</option>
                          </select>
                        </div>
                      </div>

                      {/* 우측 미리보기 및 설정 요약 */}
                      <div className="builder-preview">
                        <div className="preview-section">
                          <h3 className="preview-title">미리보기</h3>
                          <div className="preview-chart">
                            {renderCustomPreview()}
                          </div>
                        </div>

                        <div className="summary-section">
                          <h3 className="summary-title">설정 요약</h3>
                          <div className="summary-list">
                            <div className="summary-item">
                              <span className="summary-key">크기</span>
                              <span className="summary-value">{customWidgetConfig.size}</span>
                            </div>
                            <div className="summary-item">
                              <span className="summary-key">제목</span>
                              <span className="summary-value">{customWidgetConfig.title || '(미입력)'}</span>
                            </div>
                            <div className="summary-item">
                              <span className="summary-key">차트 타입</span>
                              <span className="summary-value">
                                {customWidgetConfig.chartType === 'line' && '라인 차트'}
                                {customWidgetConfig.chartType === 'bar' && '바 차트'}
                                {customWidgetConfig.chartType === 'area' && '영역 차트'}
                              </span>
                            </div>
                            <div className="summary-item">
                              <span className="summary-key">X축</span>
                              <span className="summary-value">
                                {customWidgetConfig.xAxis === 'shotNumber' && '샷 번호'}
                                {customWidgetConfig.xAxis === 'time' && '시간'}
                              </span>
                            </div>
                            <div className="summary-item">
                              <span className="summary-key">Y축</span>
                              <span className="summary-value">
                                {customWidgetConfig.yAxis === 'pressure' && '압력 (MPa)'}
                                {customWidgetConfig.yAxis === 'temperature' && '온도 (℃)'}
                                {customWidgetConfig.yAxis === 'speed' && '속도 (mm/s)'}
                                {customWidgetConfig.yAxis === 'position' && '위치 (mm)'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="builder-footer">
                      <button
                        className="builder-cancel-btn"
                        onClick={() => {
                          setShowCustomWidgetBuilder(false);
                          setSelectedWidgetType(null);
                        }}
                      >
                        취소
                      </button>
                      <button
                        className="builder-complete-btn"
                        onClick={() => {
                          setDashboardWidgets([...dashboardWidgets, {
                            id: Date.now(),
                            type: '사용자모듈',
                            size: customWidgetConfig.size,
                            slot: selectedWidgetSlot,
                            config: {
                              title: customWidgetConfig.title,
                              chartType: customWidgetConfig.chartType,
                              xAxis: customWidgetConfig.xAxis,
                              yAxis: customWidgetConfig.yAxis
                            }
                          }]);
                          setShowCustomWidgetBuilder(false);
                          setIsWidgetModalOpen(false);
                          setSelectedWidgetType(null);
                        }}
                      >
                        완료
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
          {activeTab === 'Overview' && (
            <div className="overview-grid">
              {isOverviewLoading ? (
                <div className="loading-container" style={{
                  gridColumn: '1 / -1',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '400px',
                  color: '#9CA3AF'
                }}>
                  데이터 로딩 중...
                </div>
              ) : overviewApiData ? (
                <>
                  {/* 1. 기본정보 */}
                  <div className="overview-card">
                    <div className="overview-section">
                      <h3 className="section-title">기본정보</h3>
                      <div className="info-list">
                        <div className="info-item">
                          <span className="info-label">제품명</span>
                          <span className="info-value">{overviewApiData?.basicInfo?.productName || '-'}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">샷카운트</span>
                          <span className="info-value">
                            {(overviewApiData?.basicInfo?.shotCount || 0).toLocaleString()}
                            <span className="info-unit"> 회</span>
                          </span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">사이클타임</span>
                          <span className="info-value">
                            {overviewApiData?.basicInfo?.cycleTime || 0}
                            <span className="info-unit"> s</span>
                          </span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">캐비티수</span>
                          <span className="info-value">
                            {overviewApiData?.basicInfo?.cavityCount || 0}
                            <span className="info-unit"> 개</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 2. 사출 */}
                  <div className="overview-card">
                    <div className="overview-section">
                      <h3 className="section-title">사출</h3>
                      <div className="info-list">
                        <div className="info-item">
                          <span className="info-label">최대사출압력</span>
                          <span className="info-value">
                            {overviewApiData?.injection?.maxPressure !== null && overviewApiData?.injection?.maxPressure !== undefined
                              ? overviewApiData.injection.maxPressure
                              : '-'}
                            <span className="info-unit"> MPa</span>
                          </span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">사출시작위치</span>
                          <span className="info-value">
                            {overviewApiData?.injection?.startPosition !== null && overviewApiData?.injection?.startPosition !== undefined
                              ? overviewApiData.injection.startPosition
                              : '-'}
                            <span className="info-unit"> mm</span>
                          </span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">사출전환위치</span>
                          <span className="info-value">
                            {overviewApiData?.injection?.switchPosition !== null && overviewApiData?.injection?.switchPosition !== undefined
                              ? overviewApiData.injection.switchPosition
                              : '-'}
                            <span className="info-unit"> mm</span>
                          </span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">사출시간</span>
                          <span className="info-value">
                            {overviewApiData?.injection?.time !== null && overviewApiData?.injection?.time !== undefined
                              ? overviewApiData.injection.time
                              : '-'}
                            <span className="info-unit"> s</span>
                          </span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">사출속도</span>
                          <span className="info-value">
                            {overviewApiData?.injection?.speed !== null && overviewApiData?.injection?.speed !== undefined
                              ? overviewApiData.injection.speed
                              : '-'}
                            <span className="info-unit"> mm/s</span>
                          </span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">석백시간</span>
                          <span className="info-value">
                            {overviewApiData?.injection?.suckbackTime !== null && overviewApiData?.injection?.suckbackTime !== undefined
                              ? overviewApiData.injection.suckbackTime
                              : '-'}
                            <span className="info-unit"> s</span>
                          </span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">석백속도</span>
                          <span className="info-value">
                            {overviewApiData?.injection?.suckbackSpeed !== null && overviewApiData?.injection?.suckbackSpeed !== undefined
                              ? overviewApiData.injection.suckbackSpeed
                              : '-'}
                            <span className="info-unit"> mm/s</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 3. 보압 */}
                  <div className="overview-card">
                    <div className="overview-section">
                      <h3 className="section-title">보압</h3>
                      <div className="info-list">
                        <div className="info-item">
                          <span className="info-label">보압전환모드</span>
                          <span className="info-value">{overviewApiData?.holding?.switchMode || '-'}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">보압전환입력</span>
                          <span className="info-value">
                            {overviewApiData?.holding?.switchInput || '-'}
                            <span className="info-unit"> mm</span>
                          </span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">보압1단계압력</span>
                          <span className="info-value">
                            {overviewApiData?.holding?.pressure1 || '-'}
                            <span className="info-unit"> MPa</span>
                          </span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">보압2단계압력</span>
                          <span className="info-value">
                            {overviewApiData?.holding?.pressure2 || '-'}
                            <span className="info-unit"> MPa</span>
                          </span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">보압1단계시간</span>
                          <span className="info-value">
                            {overviewApiData?.holding?.time1 || '-'}
                            <span className="info-unit"> s</span>
                          </span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">보압2단계시간</span>
                          <span className="info-value">
                            {overviewApiData?.holding?.time2 || '-'}
                            <span className="info-unit"> s</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 4. 계량 */}
                  <div className="overview-card">
                    <div className="overview-section">
                      <h3 className="section-title">계량</h3>
                      <div className="info-list">
                        <div className="info-item">
                          <span className="info-label">계량위치</span>
                          <span className="info-value">
                            {overviewApiData?.metering?.position || '-'}
                            <span className="info-unit"> mm</span>
                          </span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">계량속도</span>
                          <span className="info-value">
                            {overviewApiData?.metering?.speed || '-'}
                            <span className="info-unit"> rpm</span>
                          </span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">계량압력</span>
                          <span className="info-value">
                            {overviewApiData?.metering?.pressure || '-'}
                            <span className="info-unit"> MPa</span>
                          </span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">계량시간</span>
                          <span className="info-value">
                            {overviewApiData?.metering?.time || '-'}
                            <span className="info-unit"> s</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 5. 금형 */}
                  <div className="overview-card">
                    <div className="overview-section">
                      <h3 className="section-title">금형</h3>
                      <div className="info-list">
                        <div className="info-item">
                          <span className="info-label">금형두께</span>
                          <span className="info-value">
                            {overviewApiData?.mold?.thickness || '-'}
                            <span className="info-unit"> mm</span>
                          </span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">형개위치</span>
                          <span className="info-value">
                            {overviewApiData?.mold?.openPosition || '-'}
                            <span className="info-unit"> mm</span>
                          </span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">형폐위치</span>
                          <span className="info-value">
                            {overviewApiData?.mold?.closePosition || '-'}
                            <span className="info-unit"> mm</span>
                          </span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">형개시간</span>
                          <span className="info-value">
                            {overviewApiData?.mold?.openTime || '-'}
                            <span className="info-unit"> s</span>
                          </span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">형폐시간</span>
                          <span className="info-value">
                            {overviewApiData?.mold?.closeTime || '-'}
                            <span className="info-unit"> s</span>
                          </span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">금형온도1</span>
                          <span className="info-value">
                            {overviewApiData?.mold?.temp1 || '-'}
                            <span className="info-unit"> ℃</span>
                          </span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">금형온도2</span>
                          <span className="info-value">
                            {overviewApiData?.mold?.temp2 || '-'}
                            <span className="info-unit"> ℃</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 6. 이젝터 */}
                  <div className="overview-card">
                    <div className="overview-section">
                      <h3 className="section-title">이젝터</h3>
                      <div className="info-list">
                        <div className="info-item">
                          <span className="info-label">전진위치</span>
                          <span className="info-value">
                            {overviewApiData?.ejector?.forwardPosition || '-'}
                            <span className="info-unit"> mm</span>
                          </span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">후퇴위치</span>
                          <span className="info-value">
                            {overviewApiData?.ejector?.backwardPosition || '-'}
                            <span className="info-unit"> mm</span>
                          </span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">전진속도</span>
                          <span className="info-value">
                            {overviewApiData?.ejector?.forwardSpeed || '-'}
                            <span className="info-unit"> mm/s</span>
                          </span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">후퇴속도</span>
                          <span className="info-value">
                            {overviewApiData?.ejector?.backwardSpeed || '-'}
                            <span className="info-unit"> mm/s</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 7. 히터온도 */}
                  <div className="overview-card-wide">
                    <div className="overview-section">
                      <h3 className="section-title">히터온도</h3>
                      <div className="heater-grid">
                        {overviewApiData?.heater?.zones?.filter(zone => zone.zoneName !== '노즐온도').map((zone, idx) => {
                          // API에서 받은 실제 온도 추이 데이터 사용
                          const tempData = zone.tempTrend?.map((temp, i) => ({
                            index: i,
                            temp: parseFloat(temp)
                          })) || [];

                          // 데이터 유무 확인 (currentTemp가 null이거나 tempTrend가 비어있으면 데이터 없음)
                          const hasData = zone.currentTemp !== null && tempData.length > 0;

                          return (
                            <div key={idx} className="heater-zone">
                              <div className="heater-info">
                                <span className="heater-label">{zone.zoneName}</span>
                                <span className="heater-value">
                                  {zone.currentTemp || '-'}
                                  <span className="heater-unit">℃</span>
                                </span>
                              </div>
                              <div className="heater-chart" style={{ position: 'relative' }}>
                                <ResponsiveContainer width="100%" height={80}>
                                  <AreaChart data={tempData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                                    <defs>
                                      <linearGradient id={`colorTemp${idx}`} x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.3}/>
                                        <stop offset="95%" stopColor="#F59E0B" stopOpacity={0}/>
                                      </linearGradient>
                                    </defs>
                                    <Area
                                      type="monotone"
                                      dataKey="temp"
                                      stroke="#F59E0B"
                                      strokeWidth={1}
                                      fill={`url(#colorTemp${idx})`}
                                      isAnimationActive={true}
                                      animationDuration={1000}
                                      animationEasing="ease-in-out"
                                    />
                                  </AreaChart>
                                </ResponsiveContainer>
                                {!hasData && (
                                  <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    color: '#9CA3AF',
                                    fontSize: '12px',
                                    pointerEvents: 'none'
                                  }}>
                                    데이터가 없습니다
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="error-container" style={{
                  gridColumn: '1 / -1',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '400px',
                  color: '#EF4444'
                }}>
                  데이터를 불러올 수 없습니다.
                </div>
              )}
            </div>
          )}
          {activeTab === 'Cycle' && (
            <div className="cycle-container">
              {/* 로딩 상태 */}
              {isCycleLoading && (
                <div className="loading-message" style={{ textAlign: 'center', padding: '40px', color: '#9CA3AF' }}>
                  데이터를 불러오는 중...
                </div>
              )}

              {/* 에러 상태 */}
              {cycleError && !isCycleLoading && (
                <div className="error-message" style={{ textAlign: 'center', padding: '40px', color: '#EF4444' }}>
                  {cycleError}
                </div>
              )}

              {/* 데이터 표시 */}
              {!isCycleLoading && !cycleError && cycleData && (
                <>
                  {/* 상단 영역 */}
                  <div className="cycle-top-area">
                    <div className="cycle-controls">
                      <div className="chart-select-wrapper">
                        <label className="control-label">차트선택</label>
                        <select
                          className="chart-select"
                          value={selectedChart}
                          onChange={(e) => setSelectedChart(e.target.value)}
                        >
                          <option value="전체">전체</option>
                          <option value="사출 프로파일">사출 프로파일</option>
                          <option value="보압 프로파일">보압 프로파일</option>
                          <option value="계량 프로파일">계량 프로파일</option>
                          <option value="사이클 타임 트렌드">사이클 타임 트렌드</option>
                        </select>
                      </div>

                      <div className="checkbox-wrapper">
                        <input
                          type="checkbox"
                          id="autoScale"
                          checked={autoScale}
                          onChange={(e) => setAutoScale(e.target.checked)}
                        />
                        <label htmlFor="autoScale" className="checkbox-label">자동스케일</label>
                      </div>

                      <div className="checkbox-wrapper">
                        <input
                          type="checkbox"
                          id="dataHold"
                          checked={dataHold}
                          onChange={(e) => setDataHold(e.target.checked)}
                        />
                        <label htmlFor="dataHold" className="checkbox-label">데이터홀드</label>
                      </div>
                    </div>
                  </div>

              {/* 2x2 그리드 */}
              <div className={`cycle-grid ${selectedChart !== '전체' ? 'single-chart' : ''}`}>
                {/* 사출 프로파일 */}
                {(selectedChart === '전체' || selectedChart === '사출 프로파일') && (
                <div className="cycle-card">
                  <div className="cycle-card-header">사출 프로파일</div>
                  <div className="cycle-card-chart">
                    {cycleData.injectionProfile?.data && cycleData.injectionProfile.data.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={cycleData.injectionProfile.data[0].profile} margin={{ top: 5, right: -20, left: -20, bottom: 10 }}>
                        <defs>
                          <linearGradient id="colorPressure" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#60A5FA" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#60A5FA" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <XAxis
                          dataKey="step"
                          domain={[0, 1]}
                          type="number"
                          ticks={[0, 0.05, 0.1, 0.15, 0.2, 0.25, 0.3, 0.35, 0.4, 0.45, 0.5, 0.55, 0.6, 0.65, 0.7, 0.75, 0.8, 0.85, 0.9, 0.95, 1.0]}
                          stroke="#9CA3AF"
                          tick={{ fill: '#9CA3AF', fontSize: 10 }}
                          axisLine={{ stroke: '#4B5563' }}
                          label={{ value: '단계', position: 'insideBottom', offset: -5, fill: '#9CA3AF', fontSize: 12 }}
                        />
                        <YAxis
                          yAxisId="left"
                          stroke="#9CA3AF"
                          tick={{ fill: '#9CA3AF', fontSize: 10 }}
                          axisLine={{ stroke: '#4B5563' }}
                          domain={autoScale ? ['auto', 'auto'] : [0, 100]}
                          ticks={autoScale ? undefined : [0, 20, 40, 60, 80, 100]}
                          label={{ value: '압력(MPa)', angle: -90, position: 'center', style: { textAnchor: 'middle' }, fill: '#9CA3AF', fontSize: 11 }}
                        />
                        <YAxis
                          yAxisId="right"
                          orientation="right"
                          stroke="#9CA3AF"
                          tick={{ fill: '#9CA3AF', fontSize: 10 }}
                          axisLine={{ stroke: '#4B5563' }}
                          domain={autoScale ? ['auto', 'auto'] : [0, 100]}
                          ticks={autoScale ? undefined : [0, 20, 40, 60, 80, 100]}
                          label={{ value: '속도(mm/s)', angle: 90, position: 'center', style: { textAnchor: 'middle' }, fill: '#9CA3AF', fontSize: 11 }}
                        />
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={true} horizontal={false} />
                        <ReferenceLine yAxisId="left" y={20} stroke="#374151" strokeDasharray="3 3" />
                        <ReferenceLine yAxisId="left" y={40} stroke="#374151" strokeDasharray="3 3" />
                        <ReferenceLine yAxisId="left" y={60} stroke="#374151" strokeDasharray="3 3" />
                        <ReferenceLine yAxisId="left" y={80} stroke="#374151" strokeDasharray="3 3" />
                        <ReferenceLine yAxisId="left" y={100} stroke="#374151" strokeDasharray="3 3" />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '4px', fontSize: '12px' }}
                          labelStyle={{ color: '#9CA3AF', fontSize: '13px' }}
                        />
                        <Legend
                          wrapperStyle={{
                            position: 'absolute',
                            top: '15px',
                            right: '30px',
                            fontSize: '10px',
                            width: 'auto'
                          }}
                          iconType="line"
                          iconSize={16}
                          layout="horizontal"
                        />
                        <Area
                          yAxisId="left"
                          type="monotone"
                          dataKey="pressure"
                          stroke="#60A5FA"
                          strokeWidth={1}
                          fill="url(#colorPressure)"
                          isAnimationActive={true}
                          animationDuration={1000}
                          animationEasing="ease-in-out"
                          name={`Shot ${cycleData.injectionProfile.data[0].shotNumber} - 압력`}
                        />
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey="speed"
                          stroke="#34D399"
                          strokeWidth={1}
                          dot={false}
                          isAnimationActive={true}
                          animationDuration={1000}
                          animationEasing="ease-in-out"
                          name={`Shot ${cycleData.injectionProfile.data[0].shotNumber} - 속도`}
                        />
                        {/* Shot 2 데이터가 있으면 표시 (점선) */}
                        {cycleData.injectionProfile.data[1] && (
                          <>
                            <Line
                              yAxisId="left"
                              data={cycleData.injectionProfile.data[1].profile}
                              type="monotone"
                              dataKey="pressure"
                              stroke="#60A5FA"
                              strokeWidth={1}
                              strokeDasharray="5 5"
                              dot={false}
                              isAnimationActive={true}
                              animationDuration={1000}
                              animationEasing="ease-in-out"
                              name={`Shot ${cycleData.injectionProfile.data[1].shotNumber} - 압력 (--)`}
                            />
                            <Line
                              yAxisId="right"
                              data={cycleData.injectionProfile.data[1].profile}
                              type="monotone"
                              dataKey="speed"
                              stroke="#34D399"
                              strokeWidth={1}
                              strokeDasharray="5 5"
                              dot={false}
                              isAnimationActive={true}
                              animationDuration={1000}
                              animationEasing="ease-in-out"
                              name={`Shot ${cycleData.injectionProfile.data[1].shotNumber} - 속도 (--)`}
                            />
                          </>
                        )}
                      </ComposedChart>
                    </ResponsiveContainer>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9CA3AF' }}>
                        프로파일 데이터가 없습니다
                      </div>
                    )}
                  </div>
                </div>
                )}

                {/* 사이클 타임 트렌드 */}
                {(selectedChart === '전체' || selectedChart === '사이클 타임 트렌드') && (
                <div className="cycle-card">
                  <div className="cycle-card-header">사이클 타임 트렌드</div>
                  <div className="cycle-card-chart">
                    {cycleData.cycleTimeTrend?.data && cycleData.cycleTimeTrend.data.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={cycleData.cycleTimeTrend.data} margin={{ top: 5, right: 10, left: -35, bottom: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis
                          dataKey="shotNumber"
                          stroke="#9CA3AF"
                          tick={{ fill: '#9CA3AF', fontSize: 10 }}
                          axisLine={{ stroke: '#4B5563' }}
                          label={{ value: '샷 번호', position: 'insideBottom', offset: -5, fill: '#9CA3AF', fontSize: 12 }}
                        />
                        <YAxis
                          stroke="#9CA3AF"
                          tick={{ fill: '#9CA3AF', fontSize: 10 }}
                          axisLine={{ stroke: '#4B5563' }}
                          domain={autoScale ? ['auto', 'auto'] : [0, 'auto']}
                          label={{ value: '시간(sec)', angle: -90, position: 'center', style: { textAnchor: 'middle' }, fill: '#9CA3AF', fontSize: 11 }}
                        />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '4px', fontSize: '12px' }}
                          labelStyle={{ color: '#9CA3AF', fontSize: '13px' }}
                        />
                        <Legend
                          wrapperStyle={{
                            position: 'absolute',
                            top: '15px',
                            right: '30px',
                            fontSize: '10px',
                            width: 'auto'
                          }}
                          iconType="line"
                          iconSize={16}
                          layout="horizontal"
                        />
                        <Line
                          type="monotone"
                          dataKey="cycleTime"
                          stroke="#60A5FA"
                          strokeWidth={1}
                          dot={false}
                          isAnimationActive={true}
                          animationDuration={1000}
                          animationEasing="ease-in-out"
                          name="사이클타임"
                        />
                        <Line
                          type="monotone"
                          dataKey="injectionTime"
                          stroke="#34D399"
                          strokeWidth={1}
                          dot={false}
                          isAnimationActive={true}
                          animationDuration={1000}
                          animationEasing="ease-in-out"
                          name="사출시간"
                        />
                        <Line
                          type="monotone"
                          dataKey="holdingTime"
                          stroke="#F59E0B"
                          strokeWidth={1}
                          dot={false}
                          isAnimationActive={true}
                          animationDuration={1000}
                          animationEasing="ease-in-out"
                          name="보압시간"
                        />
                        <Line
                          type="monotone"
                          dataKey="meteringTime"
                          stroke="#A78BFA"
                          strokeWidth={1}
                          dot={false}
                          isAnimationActive={true}
                          animationDuration={1000}
                          animationEasing="ease-in-out"
                          name="계량시간"
                        />
                        <Line
                          type="monotone"
                          dataKey="coolingTime"
                          stroke="#EC4899"
                          strokeWidth={1}
                          dot={false}
                          isAnimationActive={true}
                          animationDuration={1000}
                          animationEasing="ease-in-out"
                          name="냉각시간"
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9CA3AF' }}>
                        사이클 타임 데이터가 없습니다
                      </div>
                    )}
                  </div>
                </div>
                )}

                {/* 보압 프로파일 */}
                {(selectedChart === '전체' || selectedChart === '보압 프로파일') && (
                <div className="cycle-card">
                  <div className="cycle-card-header">보압 프로파일</div>
                  <div className="cycle-card-chart">
                    {cycleData.holdingProfile?.data && cycleData.holdingProfile.data.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={cycleData.holdingProfile.data[0].profile} margin={{ top: 5, right: -20, left: -20, bottom: 10 }}>
                        <defs>
                          <linearGradient id="colorHolding" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#F59E0B" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <XAxis
                          dataKey="step"
                          domain={[0, 1]}
                          type="number"
                          ticks={[0, 0.05, 0.1, 0.15, 0.2, 0.25, 0.3, 0.35, 0.4, 0.45, 0.5, 0.55, 0.6, 0.65, 0.7, 0.75, 0.8, 0.85, 0.9, 0.95, 1.0]}
                          stroke="#9CA3AF"
                          tick={{ fill: '#9CA3AF', fontSize: 10 }}
                          axisLine={{ stroke: '#4B5563' }}
                          label={{ value: '단계', position: 'insideBottom', offset: -5, fill: '#9CA3AF', fontSize: 12 }}
                        />
                        <YAxis
                          yAxisId="left"
                          stroke="#9CA3AF"
                          tick={{ fill: '#9CA3AF', fontSize: 10 }}
                          axisLine={{ stroke: '#4B5563' }}
                          domain={autoScale ? ['auto', 'auto'] : [0, 100]}
                          ticks={autoScale ? undefined : [0, 20, 40, 60, 80, 100]}
                          label={{ value: '보압(MPa)', angle: -90, position: 'center', style: { textAnchor: 'middle' }, fill: '#9CA3AF', fontSize: 11 }}
                        />
                        <YAxis
                          yAxisId="right"
                          orientation="right"
                          stroke="#9CA3AF"
                          tick={{ fill: '#9CA3AF', fontSize: 10 }}
                          axisLine={{ stroke: '#4B5563' }}
                          domain={autoScale ? ['auto', 'auto'] : [0, 50]}
                          ticks={autoScale ? undefined : [0, 10, 20, 30, 40, 50]}
                          label={{ value: '시간(sec)', angle: 90, position: 'center', style: { textAnchor: 'middle' }, fill: '#9CA3AF', fontSize: 11 }}
                        />
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={true} horizontal={false} />
                        <ReferenceLine yAxisId="left" y={20} stroke="#374151" strokeDasharray="3 3" />
                        <ReferenceLine yAxisId="left" y={40} stroke="#374151" strokeDasharray="3 3" />
                        <ReferenceLine yAxisId="left" y={60} stroke="#374151" strokeDasharray="3 3" />
                        <ReferenceLine yAxisId="left" y={80} stroke="#374151" strokeDasharray="3 3" />
                        <ReferenceLine yAxisId="left" y={100} stroke="#374151" strokeDasharray="3 3" />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '4px', fontSize: '12px' }}
                          labelStyle={{ color: '#9CA3AF', fontSize: '13px' }}
                        />
                        <Legend
                          wrapperStyle={{
                            position: 'absolute',
                            top: '15px',
                            right: '30px',
                            fontSize: '10px',
                            width: 'auto'
                          }}
                          iconType="line"
                          iconSize={16}
                          layout="horizontal"
                        />
                        <Area
                          yAxisId="left"
                          type="monotone"
                          dataKey="holding"
                          stroke="#F59E0B"
                          strokeWidth={1}
                          fill="url(#colorHolding)"
                          isAnimationActive={true}
                          animationDuration={1000}
                          animationEasing="ease-in-out"
                          name={`Shot ${cycleData.holdingProfile.data[0].shotNumber} - 보압`}
                        />
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey="time"
                          stroke="#34D399"
                          strokeWidth={1}
                          dot={false}
                          isAnimationActive={true}
                          animationDuration={1000}
                          animationEasing="ease-in-out"
                          name={`Shot ${cycleData.holdingProfile.data[0].shotNumber} - 시간`}
                        />
                        {/* Shot 2 데이터가 있으면 표시 (점선) */}
                        {cycleData.holdingProfile.data[1] && (
                          <>
                            <Area
                              yAxisId="left"
                              data={cycleData.holdingProfile.data[1].profile}
                              type="monotone"
                              dataKey="holding"
                              stroke="#F59E0B"
                              strokeWidth={1}
                              strokeDasharray="5 5"
                              fill="none"
                              isAnimationActive={true}
                              animationDuration={1000}
                              animationEasing="ease-in-out"
                              name={`Shot ${cycleData.holdingProfile.data[1].shotNumber} - 보압 (--)`}
                            />
                            <Line
                              yAxisId="right"
                              data={cycleData.holdingProfile.data[1].profile}
                              type="monotone"
                              dataKey="time"
                              stroke="#34D399"
                              strokeWidth={1}
                              strokeDasharray="5 5"
                              dot={false}
                              isAnimationActive={true}
                              animationDuration={1000}
                              animationEasing="ease-in-out"
                              name={`Shot ${cycleData.holdingProfile.data[1].shotNumber} - 시간 (--)`}
                            />
                          </>
                        )}
                      </ComposedChart>
                    </ResponsiveContainer>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9CA3AF' }}>
                        프로파일 데이터가 없습니다
                      </div>
                    )}
                  </div>
                </div>
                )}

                {/* 계량 프로파일 */}
                {(selectedChart === '전체' || selectedChart === '계량 프로파일') && (
                <div className="cycle-card">
                  <div className="cycle-card-header">계량 프로파일</div>
                  <div className="cycle-card-chart">
                    {cycleData.meteringProfile?.data && cycleData.meteringProfile.data.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={cycleData.meteringProfile.data[0].profile} margin={{ top: 5, right: -20, left: -20, bottom: 10 }}>
                        <defs>
                          <linearGradient id="colorMetering" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#A78BFA" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#A78BFA" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <XAxis
                          dataKey="step"
                          domain={[0, 1]}
                          type="number"
                          ticks={[0, 0.05, 0.1, 0.15, 0.2, 0.25, 0.3, 0.35, 0.4, 0.45, 0.5, 0.55, 0.6, 0.65, 0.7, 0.75, 0.8, 0.85, 0.9, 0.95, 1.0]}
                          stroke="#9CA3AF"
                          tick={{ fill: '#9CA3AF', fontSize: 10 }}
                          axisLine={{ stroke: '#4B5563' }}
                          label={{ value: '단계', position: 'insideBottom', offset: -5, fill: '#9CA3AF', fontSize: 12 }}
                        />
                        <YAxis
                          yAxisId="left"
                          stroke="#9CA3AF"
                          tick={{ fill: '#9CA3AF', fontSize: 10 }}
                          axisLine={{ stroke: '#4B5563' }}
                          domain={autoScale ? ['auto', 'auto'] : [0, 100]}
                          ticks={autoScale ? undefined : [0, 20, 40, 60, 80, 100]}
                          label={{ value: '배압(MPa)', angle: -90, position: 'center', style: { textAnchor: 'middle' }, fill: '#9CA3AF', fontSize: 11 }}
                        />
                        <YAxis
                          yAxisId="right"
                          orientation="right"
                          stroke="#9CA3AF"
                          tick={{ fill: '#9CA3AF', fontSize: 10 }}
                          axisLine={{ stroke: '#4B5563' }}
                          domain={autoScale ? ['auto', 'auto'] : [0, 60]}
                          ticks={autoScale ? undefined : [0, 15, 30, 45, 60]}
                          label={{ value: '쿠션위치(mm)', angle: 90, position: 'center', style: { textAnchor: 'middle' }, fill: '#9CA3AF', fontSize: 11 }}
                        />
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={true} horizontal={false} />
                        <ReferenceLine yAxisId="left" y={20} stroke="#374151" strokeDasharray="3 3" />
                        <ReferenceLine yAxisId="left" y={40} stroke="#374151" strokeDasharray="3 3" />
                        <ReferenceLine yAxisId="left" y={60} stroke="#374151" strokeDasharray="3 3" />
                        <ReferenceLine yAxisId="left" y={80} stroke="#374151" strokeDasharray="3 3" />
                        <ReferenceLine yAxisId="left" y={100} stroke="#374151" strokeDasharray="3 3" />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '4px', fontSize: '12px' }}
                          labelStyle={{ color: '#9CA3AF', fontSize: '13px' }}
                        />
                        <Legend
                          wrapperStyle={{
                            position: 'absolute',
                            top: '15px',
                            right: '30px',
                            fontSize: '10px',
                            width: 'auto'
                          }}
                          iconType="line"
                          iconSize={16}
                          layout="horizontal"
                        />
                        <Area
                          yAxisId="left"
                          type="monotone"
                          dataKey="backPressure"
                          stroke="#A78BFA"
                          strokeWidth={1}
                          fill="url(#colorMetering)"
                          isAnimationActive={true}
                          animationDuration={1000}
                          animationEasing="ease-in-out"
                          name={`Shot ${cycleData.meteringProfile.data[0].shotNumber} - 배압`}
                        />
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey="cushion"
                          stroke="#EC4899"
                          strokeWidth={1}
                          dot={false}
                          isAnimationActive={true}
                          animationDuration={1000}
                          animationEasing="ease-in-out"
                          name={`Shot ${cycleData.meteringProfile.data[0].shotNumber} - 쿠션위치`}
                        />
                        {/* Shot 2 데이터가 있으면 표시 (점선) */}
                        {cycleData.meteringProfile.data[1] && (
                          <>
                            <Area
                              yAxisId="left"
                              data={cycleData.meteringProfile.data[1].profile}
                              type="monotone"
                              dataKey="backPressure"
                              stroke="#A78BFA"
                              strokeWidth={1}
                              strokeDasharray="5 5"
                              fill="none"
                              isAnimationActive={true}
                              animationDuration={1000}
                              animationEasing="ease-in-out"
                              name={`Shot ${cycleData.meteringProfile.data[1].shotNumber} - 배압 (--)`}
                            />
                            <Line
                              yAxisId="right"
                              data={cycleData.meteringProfile.data[1].profile}
                              type="monotone"
                              dataKey="cushion"
                              stroke="#EC4899"
                              strokeWidth={1}
                              strokeDasharray="5 5"
                              dot={false}
                              isAnimationActive={true}
                              animationDuration={1000}
                              animationEasing="ease-in-out"
                              name={`Shot ${cycleData.meteringProfile.data[1].shotNumber} - 쿠션위치 (--)`}
                            />
                          </>
                        )}
                      </ComposedChart>
                    </ResponsiveContainer>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9CA3AF' }}>
                        프로파일 데이터가 없습니다
                      </div>
                    )}
                  </div>
                </div>
                )}
              </div>

              {/* 하단 영역 - 사이클 통계 */}
              {cycleData.cycleStatistics && (
                <div className="cycle-bottom-area">
                  <div className="cycle-stats-header">사이클 통계</div>
                  <div className="cycle-stats-content">
                    <div className="cycle-stat-item">
                      <span className="cycle-stat-label">평균사이클</span>
                      <span className="cycle-stat-value">
                        {cycleData.cycleStatistics.averageCycle.toFixed(2)}
                        <span className="cycle-stat-unit">초</span>
                      </span>
                    </div>
                    <div className="cycle-stat-item">
                      <span className="cycle-stat-label">최소사이클</span>
                      <span className="cycle-stat-value">
                        {cycleData.cycleStatistics.minCycle.toFixed(2)}
                        <span className="cycle-stat-unit">초</span>
                      </span>
                    </div>
                    <div className="cycle-stat-item">
                      <span className="cycle-stat-label">최대사이클</span>
                      <span className="cycle-stat-value">
                        {cycleData.cycleStatistics.maxCycle.toFixed(2)}
                        <span className="cycle-stat-unit">초</span>
                      </span>
                    </div>
                    <div className="cycle-stat-item">
                      <span className="cycle-stat-label">표준편차</span>
                      <span className="cycle-stat-value">
                        {cycleData.cycleStatistics.stdDeviation.toFixed(2)}
                        <span className="cycle-stat-unit">초</span>
                      </span>
                    </div>
                    <div className="cycle-stat-item">
                      <span className="cycle-stat-label">최근사이클</span>
                      <span className="cycle-stat-value">
                        {cycleData.cycleStatistics.latestCycle.toFixed(2)}
                        <span className="cycle-stat-unit">초</span>
                      </span>
                    </div>
                  </div>
                </div>
              )}
                </>
              )}
            </div>
          )}
          {activeTab === '온도' && (
            <div className="temp-tab-container">
              {isTemperatureLoading && (
                <div className="loading-message">데이터를 불러오는 중...</div>
              )}

              {temperatureError && (
                <div className="error-message">{temperatureError}</div>
              )}

              {!isTemperatureLoading && !temperatureError && temperatureData && (
                <div className="temp-grid">
                {/* Zone별 Heater - 1열 × 2행 */}
                <div className="temp-card temp-zone-heater">
                  <div className="temp-card-header">Zone별 Heater</div>
                  <div className="temp-card-content">
                    <div className="zone-heater-grid">
                      {temperatureData.zoneHeaters.map((zone, idx) => {
                        return (
                          <div key={idx} className="zone-column">
                            <div className="zone-column-header">{zone.zoneName}</div>
                            <div className="zone-column-body">
                              <div className="zone-temp-row">
                                <span className="zone-temp-label">현재</span>
                                <span className="zone-temp-current">{zone.currentTemp.toFixed(1)}°C</span>
                              </div>
                              <div className="zone-temp-row">
                                <span className="zone-temp-label">설정</span>
                                <span className="zone-temp-set">{zone.setTemp.toFixed(1)}°C</span>
                              </div>
                              <div className="zone-temp-row">
                                <span className="zone-temp-label">편차</span>
                                <span className={`zone-temp-deviation ${zone.status}`}>
                                  ±{zone.deviation.toFixed(1)}°C
                                </span>
                              </div>
                            </div>
                            <div className={`zone-status ${zone.status}`}>
                              {zone.status === 'normal' ? '정상' : zone.status === 'warning' ? '경고' : '오프라인'}
                            </div>
                          </div>
                        );
                      })}
                      {/* 6번째 빈 칸 */}
                      <div className="zone-column-empty"></div>
                    </div>
                  </div>
                </div>

                {/* 온도 트렌드 - 2열 × 2행 */}
                <div className="temp-card temp-trend">
                  <div className="temp-card-header">온도 트렌드</div>
                  <div className="temp-card-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={temperatureData.tempTrend.data} margin={{ top: 5, right: 10, left: -35, bottom: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis
                          dataKey="shotNumber"
                          stroke="#9CA3AF"
                          tick={{ fill: '#9CA3AF', fontSize: 10 }}
                          axisLine={{ stroke: '#4B5563' }}
                          label={{ value: '샷 번호', position: 'insideBottom', offset: -5, fill: '#9CA3AF', fontSize: 12 }}
                          interval={1}
                        />
                        <YAxis
                          stroke="#9CA3AF"
                          tick={{ fill: '#9CA3AF', fontSize: 10 }}
                          axisLine={{ stroke: '#4B5563' }}
                          label={{ value: '온도(℃)', angle: -90, position: 'center', style: { textAnchor: 'middle' }, fill: '#9CA3AF', fontSize: 11 }}
                          domain={[180, 280]}
                        />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '4px', fontSize: '12px' }}
                          labelStyle={{ color: '#9CA3AF', fontSize: '13px' }}
                        />
                        <Legend
                          wrapperStyle={{
                            position: 'absolute',
                            top: '15px',
                            right: '30px',
                            fontSize: '10px',
                            width: 'auto'
                          }}
                          iconType="line"
                          iconSize={16}
                          layout="horizontal"
                        />
                        {/* Zone 현재 온도 (실선) */}
                        <Line
                          type="monotone"
                          dataKey="zone1"
                          stroke="#EF4444"
                          strokeWidth={1}
                          dot={false}
                          isAnimationActive={true}
                          animationDuration={1000}
                          animationEasing="ease-in-out"
                          name="Zone1 현재"
                        />
                        <Line
                          type="monotone"
                          dataKey="zone2"
                          stroke="#F59E0B"
                          strokeWidth={1}
                          dot={false}
                          isAnimationActive={true}
                          animationDuration={1000}
                          animationEasing="ease-in-out"
                          name="Zone2 현재"
                        />
                        <Line
                          type="monotone"
                          dataKey="zone3"
                          stroke="#10B981"
                          strokeWidth={1}
                          dot={false}
                          isAnimationActive={true}
                          animationDuration={1000}
                          animationEasing="ease-in-out"
                          name="Zone3 현재"
                        />
                        <Line
                          type="monotone"
                          dataKey="zone4"
                          stroke="#3B82F6"
                          strokeWidth={1}
                          dot={false}
                          isAnimationActive={true}
                          animationDuration={1000}
                          animationEasing="ease-in-out"
                          name="Zone4 현재"
                        />
                        <Line
                          type="monotone"
                          dataKey="zone5"
                          stroke="#8B5CF6"
                          strokeWidth={1}
                          dot={false}
                          isAnimationActive={true}
                          animationDuration={1000}
                          animationEasing="ease-in-out"
                          name="Zone5 현재"
                        />
                        {/* Zone 설정 온도 (점선) */}
                        <Line
                          type="monotone"
                          dataKey="setTemp1"
                          stroke="#EF4444"
                          strokeWidth={1}
                          strokeDasharray="5 5"
                          dot={false}
                          isAnimationActive={true}
                          animationDuration={1000}
                          animationEasing="ease-in-out"
                          name="Zone1 설정"
                        />
                        <Line
                          type="monotone"
                          dataKey="setTemp2"
                          stroke="#F59E0B"
                          strokeWidth={1}
                          strokeDasharray="5 5"
                          dot={false}
                          isAnimationActive={true}
                          animationDuration={1000}
                          animationEasing="ease-in-out"
                          name="Zone2 설정"
                        />
                        <Line
                          type="monotone"
                          dataKey="setTemp3"
                          stroke="#10B981"
                          strokeWidth={1}
                          strokeDasharray="5 5"
                          dot={false}
                          isAnimationActive={true}
                          animationDuration={1000}
                          animationEasing="ease-in-out"
                          name="Zone3 설정"
                        />
                        <Line
                          type="monotone"
                          dataKey="setTemp4"
                          stroke="#3B82F6"
                          strokeWidth={1}
                          strokeDasharray="5 5"
                          dot={false}
                          isAnimationActive={true}
                          animationDuration={1000}
                          animationEasing="ease-in-out"
                          name="Zone4 설정"
                        />
                        <Line
                          type="monotone"
                          dataKey="setTemp5"
                          stroke="#8B5CF6"
                          strokeWidth={1}
                          strokeDasharray="5 5"
                          dot={false}
                          isAnimationActive={true}
                          animationDuration={1000}
                          animationEasing="ease-in-out"
                          name="Zone5 설정"
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* 하단 왼쪽 - 금형온도 */}
                <div className="temp-card temp-bottom-left">
                  <div className="temp-card-content">
                    <table className="mold-temp-table">
                      <thead>
                        <tr>
                          <th>항목</th>
                          <th>값</th>
                          <th>상태</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>평균온도</td>
                          <td>{temperatureData.moldTemperature.averageTemp.toFixed(1)}°C</td>
                          <td>
                            <span className={`status-badge ${
                              temperatureData.moldTemperature.averageTemp > 200 ? 'normal' : 'warning'
                            }`}>
                              {temperatureData.moldTemperature.averageTemp > 200 ? '정상' : '경고'}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td>최대편차</td>
                          <td>{temperatureData.moldTemperature.maxDeviation.toFixed(1)}°C</td>
                          <td>
                            <span className={`status-badge ${
                              temperatureData.moldTemperature.maxDeviation <= 5 ? 'normal' : 'warning'
                            }`}>
                              {temperatureData.moldTemperature.maxDeviation <= 5 ? '정상' : '경고'}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td>경고 Zone 수</td>
                          <td>{temperatureData.moldTemperature.warningZoneCount}개</td>
                          <td>
                            <span className={`status-badge ${
                              temperatureData.moldTemperature.warningZoneCount === 0 ? 'normal' : 'warning'
                            }`}>
                              {temperatureData.moldTemperature.warningZoneCount === 0 ? '정상' : '경고'}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td>오일온도</td>
                          <td>{temperatureData.moldTemperature.oilTemp.toFixed(1)}°C</td>
                          <td>
                            <span className={`status-badge ${
                              temperatureData.moldTemperature.oilTemp >= 40 &&
                              temperatureData.moldTemperature.oilTemp <= 55 ? 'normal' : 'warning'
                            }`}>
                              {temperatureData.moldTemperature.oilTemp >= 40 &&
                               temperatureData.moldTemperature.oilTemp <= 55 ? '정상' : '경고'}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* 온도편차현황 - 2열 × 1행 */}
                <div className="temp-card temp-deviation">
                  <div className="temp-card-header">온도편차현황</div>
                  <div className="temp-card-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={temperatureData.deviationStatus}
                        margin={{ top: 10, right: 20, left: -20, bottom: 10 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis
                          dataKey="zoneName"
                          stroke="#9CA3AF"
                          tick={{ fill: '#9CA3AF', fontSize: 11 }}
                          axisLine={{ stroke: '#4B5563' }}
                        />
                        <YAxis
                          stroke="#9CA3AF"
                          tick={{ fill: '#9CA3AF', fontSize: 10 }}
                          axisLine={{ stroke: '#4B5563' }}
                          label={{ value: '편차(℃)', angle: -90, position: 'center', style: { textAnchor: 'middle' }, fill: '#9CA3AF', fontSize: 11 }}
                          domain={[-5, 5]}
                        />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '4px', fontSize: '12px' }}
                          labelStyle={{ color: '#9CA3AF', fontSize: '13px' }}
                        />
                        <Bar
                          dataKey="deviation"
                          fill="#3B82F6"
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
              )}
            </div>
          )}
          {activeTab === '구동부' && (
            <div className="drive-container">
              {isDriveLoading && !driveData ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', color: '#9CA3AF' }}>
                  로딩 중...
                </div>
              ) : driveError ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', color: '#EF4444' }}>
                  {driveError}
                </div>
              ) : driveData ? (
              <div className="drive-grid">
                {/* 1행 - 3개 영역 */}
                {/* 사출 구동부 */}
                <div className="drive-card drive-card-1">
                  <div className="drive-card-header">사출 구동부</div>
                  <div className="drive-card-content">
                    <div className="drive-metrics">
                      {/* 압력 */}
                      <div className="drive-metric-item">
                        <div className="drive-metric-label">압력</div>
                        <div className="drive-metric-value">{driveData.injectionDrive.pressure.toFixed(1)} <span className="drive-unit">MPa</span></div>
                        <div className="drive-gauge-container">
                          <div className="drive-gauge-bg">
                            <div className="drive-gauge-fill" style={{ width: `${driveData.injectionDrive.pressurePercent}%` }}></div>
                          </div>
                          <span className="drive-gauge-percent">{driveData.injectionDrive.pressurePercent.toFixed(1)}%</span>
                        </div>
                      </div>
                      {/* 속도 */}
                      <div className="drive-metric-item">
                        <div className="drive-metric-label">속도</div>
                        <div className="drive-metric-value">{driveData.injectionDrive.speed.toFixed(1)} <span className="drive-unit">mm/s</span></div>
                        <div className="drive-gauge-container">
                          <div className="drive-gauge-bg">
                            <div className="drive-gauge-fill" style={{ width: `${driveData.injectionDrive.speedPercent}%` }}></div>
                          </div>
                          <span className="drive-gauge-percent">{driveData.injectionDrive.speedPercent.toFixed(1)}%</span>
                        </div>
                      </div>
                      {/* 위치 */}
                      <div className="drive-metric-item">
                        <div className="drive-metric-label">위치</div>
                        <div className="drive-metric-value">{driveData.injectionDrive.position.toFixed(1)} <span className="drive-unit">mm</span></div>
                        <div className="drive-gauge-container">
                          <div className="drive-gauge-bg">
                            <div className="drive-gauge-fill" style={{ width: `${driveData.injectionDrive.positionPercent}%` }}></div>
                          </div>
                          <span className="drive-gauge-percent">{driveData.injectionDrive.positionPercent.toFixed(1)}%</span>
                        </div>
                      </div>
                      {/* 작동상태 */}
                      <div className="drive-status-row">
                        <span className="drive-status-label">작동상태</span>
                        <span className="drive-status-time">{driveData.injectionDrive.elapsedTime.toFixed(2)} sec</span>
                        <span className={`drive-status-badge ${driveData.injectionDrive.status === '작동' ? 'running' : driveData.injectionDrive.status === '대기' ? 'idle' : 'stopped'}`}>
                          {driveData.injectionDrive.status}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 계량 구동부 */}
                <div className="drive-card drive-card-2">
                  <div className="drive-card-header">계량 구동부</div>
                  <div className="drive-card-content">
                    <div className="drive-metrics">
                      {/* 압력 */}
                      <div className="drive-metric-item">
                        <div className="drive-metric-label">압력</div>
                        <div className="drive-metric-value">{driveData.meteringDrive.pressure.toFixed(1)} <span className="drive-unit">MPa</span></div>
                        <div className="drive-gauge-container">
                          <div className="drive-gauge-bg">
                            <div className="drive-gauge-fill" style={{ width: `${driveData.meteringDrive.pressurePercent}%` }}></div>
                          </div>
                          <span className="drive-gauge-percent">{driveData.meteringDrive.pressurePercent.toFixed(1)}%</span>
                        </div>
                      </div>
                      {/* 속도 */}
                      <div className="drive-metric-item">
                        <div className="drive-metric-label">속도</div>
                        <div className="drive-metric-value">{driveData.meteringDrive.speed.toFixed(1)} <span className="drive-unit">mm/s</span></div>
                        <div className="drive-gauge-container">
                          <div className="drive-gauge-bg">
                            <div className="drive-gauge-fill" style={{ width: `${driveData.meteringDrive.speedPercent}%` }}></div>
                          </div>
                          <span className="drive-gauge-percent">{driveData.meteringDrive.speedPercent.toFixed(1)}%</span>
                        </div>
                      </div>
                      {/* 위치 */}
                      <div className="drive-metric-item">
                        <div className="drive-metric-label">위치</div>
                        <div className="drive-metric-value">{driveData.meteringDrive.position.toFixed(1)} <span className="drive-unit">mm</span></div>
                        <div className="drive-gauge-container">
                          <div className="drive-gauge-bg">
                            <div className="drive-gauge-fill" style={{ width: `${driveData.meteringDrive.positionPercent}%` }}></div>
                          </div>
                          <span className="drive-gauge-percent">{driveData.meteringDrive.positionPercent.toFixed(1)}%</span>
                        </div>
                      </div>
                      {/* 작동상태 */}
                      <div className="drive-status-row">
                        <span className="drive-status-label">작동상태</span>
                        <span className="drive-status-time">{driveData.meteringDrive.elapsedTime.toFixed(2)} sec</span>
                        <span className={`drive-status-badge ${driveData.meteringDrive.status === '작동' ? 'running' : driveData.meteringDrive.status === '대기' ? 'idle' : 'stopped'}`}>
                          {driveData.meteringDrive.status}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 보압 구동부 */}
                <div className="drive-card drive-card-3">
                  <div className="drive-card-header">보압 구동부</div>
                  <div className="drive-card-content">
                    <div className="drive-metrics">
                      {/* 압력 */}
                      <div className="drive-metric-item">
                        <div className="drive-metric-label">압력</div>
                        <div className="drive-metric-value">{driveData.holdingDrive.pressure.toFixed(1)} <span className="drive-unit">MPa</span></div>
                        <div className="drive-gauge-container">
                          <div className="drive-gauge-bg">
                            <div className="drive-gauge-fill" style={{ width: `${driveData.holdingDrive.pressurePercent}%` }}></div>
                          </div>
                          <span className="drive-gauge-percent">{driveData.holdingDrive.pressurePercent.toFixed(1)}%</span>
                        </div>
                      </div>
                      {/* 속도 */}
                      <div className="drive-metric-item">
                        <div className="drive-metric-label">속도</div>
                        <div className="drive-metric-value">{driveData.holdingDrive.speed.toFixed(1)} <span className="drive-unit">mm/s</span></div>
                        <div className="drive-gauge-container">
                          <div className="drive-gauge-bg">
                            <div className="drive-gauge-fill" style={{ width: `${driveData.holdingDrive.speedPercent}%` }}></div>
                          </div>
                          <span className="drive-gauge-percent">{driveData.holdingDrive.speedPercent.toFixed(1)}%</span>
                        </div>
                      </div>
                      {/* 위치 */}
                      <div className="drive-metric-item">
                        <div className="drive-metric-label">위치</div>
                        <div className="drive-metric-value">{driveData.holdingDrive.position.toFixed(1)} <span className="drive-unit">mm</span></div>
                        <div className="drive-gauge-container">
                          <div className="drive-gauge-bg">
                            <div className="drive-gauge-fill" style={{ width: `${driveData.holdingDrive.positionPercent}%` }}></div>
                          </div>
                          <span className="drive-gauge-percent">{driveData.holdingDrive.positionPercent.toFixed(1)}%</span>
                        </div>
                      </div>
                      {/* 작동상태 */}
                      <div className="drive-status-row">
                        <span className="drive-status-label">작동상태</span>
                        <span className="drive-status-time">{driveData.holdingDrive.elapsedTime.toFixed(2)} sec</span>
                        <span className={`drive-status-badge ${driveData.holdingDrive.status === '작동' ? 'running' : driveData.holdingDrive.status === '대기' ? 'idle' : 'stopped'}`}>
                          {driveData.holdingDrive.status}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 2행 - 2개 영역 */}
                {/* 압력 트렌드 */}
                <div className="drive-card drive-card-4">
                  <div className="drive-card-header">압력 트렌드</div>
                  <div className="drive-card-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart
                        data={driveData.pressureTrend.data}
                        margin={{ top: 5, right: 10, left: -25, bottom: -5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis
                          dataKey="shotNumber"
                          stroke="#9CA3AF"
                          tick={{ fill: '#9CA3AF', fontSize: 11 }}
                          axisLine={{ stroke: '#4B5563' }}
                          label={{ value: '샷 번호', position: 'insideBottom', offset: -5, fill: '#9CA3AF', fontSize: 12 }}
                          interval={1}
                        />
                        <YAxis
                          stroke="#9CA3AF"
                          tick={{ fill: '#9CA3AF', fontSize: 11 }}
                          axisLine={{ stroke: '#4B5563' }}
                          label={{ value: '압력(MPa)', angle: -90, position: 'center', style: { textAnchor: 'middle' }, fill: '#9CA3AF', fontSize: 11 }}
                        />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '4px', fontSize: '12px' }}
                          labelStyle={{ color: '#9CA3AF', fontSize: '13px' }}
                        />
                        <Legend
                          wrapperStyle={{
                            position: 'absolute',
                            top: '15px',
                            right: '30px',
                            fontSize: '10px',
                            width: 'auto'
                          }}
                          iconType="line"
                          iconSize={16}
                          layout="horizontal"
                        />
                        <Line
                          type="monotone"
                          dataKey="injection"
                          stroke="#60A5FA"
                          strokeWidth={1}
                          dot={false}
                          isAnimationActive={true}
                          animationDuration={1000}
                          animationEasing="ease-in-out"
                          name="사출압력"
                        />
                        <Line
                          type="monotone"
                          dataKey="holding"
                          stroke="#F59E0B"
                          strokeWidth={1}
                          dot={false}
                          isAnimationActive={true}
                          animationDuration={1000}
                          animationEasing="ease-in-out"
                          name="보압압력"
                        />
                        <Line
                          type="monotone"
                          dataKey="metering"
                          stroke="#A78BFA"
                          strokeWidth={1}
                          dot={false}
                          isAnimationActive={true}
                          animationDuration={1000}
                          animationEasing="ease-in-out"
                          name="계량압력"
                        />
                        <Line
                          type="monotone"
                          dataKey="backPressure"
                          stroke="#10B981"
                          strokeWidth={1}
                          dot={false}
                          isAnimationActive={true}
                          animationDuration={1000}
                          animationEasing="ease-in-out"
                          name="배압"
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* 모터 상태 */}
                <div className="drive-card drive-card-5">
                  <div className="drive-card-header">모터 상태</div>
                  <div className="drive-card-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart
                        data={driveData.motorStatus.data}
                        margin={{ top: 5, right: 10, left: -25, bottom: 10 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis
                          dataKey="shotNumber"
                          stroke="#9CA3AF"
                          tick={{ fill: '#9CA3AF', fontSize: 11 }}
                          axisLine={{ stroke: '#4B5563' }}
                          label={{ value: '샷 번호', position: 'insideBottom', offset: -5, fill: '#9CA3AF', fontSize: 12 }}
                          interval={1}
                        />
                        <YAxis
                          stroke="#9CA3AF"
                          tick={{ fill: '#9CA3AF', fontSize: 11 }}
                          axisLine={{ stroke: '#4B5563' }}
                          label={{ value: '상태', angle: -90, position: 'center', style: { textAnchor: 'middle' }, fill: '#9CA3AF', fontSize: 11 }}
                          domain={[0, 1]}
                          ticks={[0, 1]}
                          tickFormatter={(value) => {
                            const labels = { 0: 'OFF', 1: 'ON' };
                            return labels[value] || '';
                          }}
                        />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '4px', fontSize: '12px' }}
                          labelStyle={{ color: '#9CA3AF', fontSize: '13px' }}
                          formatter={(value) => {
                            const labels = { 0: 'OFF', 1: 'ON' };
                            return [labels[value] || '', '모터 상태'];
                          }}
                        />
                        <Line
                          type="stepAfter"
                          dataKey="status"
                          stroke="#22C55E"
                          strokeWidth={1}
                          dot={false}
                          isAnimationActive={true}
                          animationDuration={1000}
                          animationEasing="ease-in-out"
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* 3행 - 배압 모니터링 */}
                <div className="drive-card drive-card-6">
                  <div className="drive-card-header">배압 모니터링</div>
                  <div className="drive-card-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart
                        data={driveData.backPressureTrend.data}
                        margin={{ top: 5, right: 10, left: -25, bottom: -5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis
                          dataKey="shotNumber"
                          stroke="#9CA3AF"
                          tick={{ fill: '#9CA3AF', fontSize: 11 }}
                          axisLine={{ stroke: '#4B5563' }}
                          label={{ value: '샷 번호', position: 'insideBottom', offset: -5, fill: '#9CA3AF', fontSize: 12 }}
                          interval={1}
                        />
                        <YAxis
                          stroke="#9CA3AF"
                          tick={{ fill: '#9CA3AF', fontSize: 11 }}
                          axisLine={{ stroke: '#4B5563' }}
                          label={{ value: '배압 (MPa)', angle: -90, position: 'center', style: { textAnchor: 'middle' }, fill: '#9CA3AF', fontSize: 11 }}
                          domain={[0, 10]}
                        />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '6px', fontSize: '12px' }}
                          labelStyle={{ color: '#9CA3AF', fontSize: '13px' }}
                          itemStyle={{ color: '#60A5FA' }}
                        />
                        <Legend
                          wrapperStyle={{
                            position: 'absolute',
                            top: '15px',
                            right: '30px',
                            fontSize: '10px',
                            width: 'auto'
                          }}
                          iconType="line"
                          iconSize={16}
                          layout="horizontal"
                        />
                        <Line
                          type="monotone"
                          dataKey="backPressure"
                          stroke="#10B981"
                          strokeWidth={1}
                          dot={false}
                          name="배압"
                        />
                        <Line
                          type="monotone"
                          dataKey="upperLimit"
                          stroke="#EF4444"
                          strokeWidth={1}
                          strokeDasharray="5 5"
                          dot={false}
                          name="상한"
                        />
                        <Line
                          type="monotone"
                          dataKey="lowerLimit"
                          stroke="#F59E0B"
                          strokeWidth={1}
                          strokeDasharray="5 5"
                          dot={false}
                          name="하한"
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* 4행 - 구동부 작동이력 */}
                <div className="drive-card drive-card-7">
                  <div className="drive-card-header">구동부 작동이력</div>
                  <div className="drive-history-table-container">
                    <table className="drive-history-table">
                      <thead>
                        <tr>
                          <th>시간</th>
                          <th>구동부</th>
                          <th>동작</th>
                          <th>압력 (MPa)</th>
                          <th>속도 (mm/s)</th>
                          <th>위치 (mm)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {driveData.driveHistory.map((item, index) => (
                          <tr key={index}>
                            <td>{new Date(item.time).toLocaleTimeString('ko-KR')}</td>
                            <td>{item.driveType}</td>
                            <td>{item.action}</td>
                            <td>{item.pressure.toFixed(1)}</td>
                            <td>{item.speed.toFixed(1)}</td>
                            <td>{item.position.toFixed(1)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              ) : null}
            </div>
          )}
          {activeTab === '알람' && (() => {
            // 타임라인 데이터를 일자 단위로 보간
            const fillTimelineData = (rawData) => {
              if (!Array.isArray(rawData) || rawData.length === 0) return [];

              // 데이터를 맵으로 변환
              const dataMap = {};
              rawData.forEach(d => {
                dataMap[d.time] = d.count;
              });

              // 날짜별로 0으로 채우기 (이미 백엔드에서 일별 집계되어 오므로 그대로 사용)
              return rawData.map(d => ({
                time: d.time,
                count: d.count || 0
              }));
            };

            const processedTimelineData = alarmData?.timeline?.data
              ? fillTimelineData(alarmData.timeline.data)
              : [];

            return (
            <div className="alarm-container">
              {isAlarmLoading && !alarmData ? (
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                  <p>알람 데이터를 불러오는 중...</p>
                </div>
              ) : alarmError ? (
                <div className="error-container">
                  <p>{alarmError}</p>
                </div>
              ) : alarmData ? (
              <div className="alarm-grid">
                {/* 1행 - 알람 현황 (전체 너비) */}
                <div className="alarm-card alarm-card-1">
                  <div className="alarm-card-header">알람 현황</div>
                  <div className="alarm-card-content">
                    <div className="alarm-summary">
                      <div className="alarm-summary-item critical">
                        <div className="alarm-summary-label">Critical</div>
                        <div className="alarm-summary-count">{alarmData?.summary?.critical || 0}</div>
                      </div>
                      <div className="alarm-summary-item warning">
                        <div className="alarm-summary-label">Warning</div>
                        <div className="alarm-summary-count">{alarmData?.summary?.warning || 0}</div>
                      </div>
                      <div className="alarm-summary-item info">
                        <div className="alarm-summary-label">Info</div>
                        <div className="alarm-summary-count">{alarmData?.summary?.info || 0}</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 2-4행 왼쪽 - 알람 이력 */}
                <div className="alarm-card alarm-card-2">
                  <div className="alarm-card-header">알람 이력</div>
                  <div className="alarm-history-table-container">
                    <table className="alarm-history-table">
                      <thead>
                        <tr>
                          <th>시간</th>
                          <th>심각도</th>
                          <th>코드</th>
                          <th>설명</th>
                          <th>상태</th>
                          <th>확인</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(alarmData?.alarmHistory || []).map((alarm, index) => (
                          <tr key={index}>
                            <td>{alarm.time ? new Date(alarm.time).toLocaleString('ko-KR', {
                              month: '2-digit',
                              day: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit'
                            }) : '-'}</td>
                            <td><span className={`severity-badge ${alarm.severity.toLowerCase()}`}>{alarm.severity}</span></td>
                            <td>{alarm.code}</td>
                            <td>{alarm.message}</td>
                            <td><span className={`alarm-status-badge ${alarm.status === 'active' ? 'active' : 'resolved'}`}>{alarm.status === 'active' ? '발생' : '해결'}</span></td>
                            <td><span className={`alarm-confirm-badge ${alarm.acknowledged ? 'confirmed' : 'unconfirmed'}`}>{alarm.acknowledged ? '확인' : '미확인'}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* 2행 오른쪽 - 알람 파레토 분석 */}
                <div className="alarm-card alarm-card-3">
                  <div className="alarm-card-header">알람 파레토 분석</div>
                  <div className="alarm-card-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart
                        data={Array.isArray(alarmData?.paretoAnalysis?.data) ? alarmData.paretoAnalysis.data : []}
                        margin={{ top: 20, right: 10, left: -25, bottom: 10 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis
                          dataKey="code"
                          stroke="#9CA3AF"
                          tick={{ fill: '#9CA3AF', fontSize: 11 }}
                          axisLine={{ stroke: '#4B5563' }}
                          label={{ value: '알람 코드', position: 'insideBottom', offset: -5, fill: '#9CA3AF', fontSize: 12 }}
                        />
                        <YAxis
                          yAxisId="left"
                          stroke="#9CA3AF"
                          tick={{ fill: '#9CA3AF', fontSize: 11 }}
                          axisLine={{ stroke: '#4B5563' }}
                          label={{ value: '발생 횟수', angle: -90, position: 'center', style: { textAnchor: 'middle' }, fill: '#9CA3AF', fontSize: 11 }}
                        />
                        <YAxis
                          yAxisId="right"
                          orientation="right"
                          stroke="#9CA3AF"
                          tick={{ fill: '#9CA3AF', fontSize: 11 }}
                          axisLine={{ stroke: '#4B5563' }}
                          label={{ value: '누적 비율 (%)', angle: 90, position: 'center', style: { textAnchor: 'middle' }, fill: '#9CA3AF', fontSize: 11 }}
                          domain={[0, 100]}
                        />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '6px', fontSize: '12px' }}
                          labelStyle={{ color: '#9CA3AF', fontSize: '13px' }}
                        />
                        <Bar yAxisId="left" dataKey="count" fill="#3B82F6" name="발생 횟수" />
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey="cumulative"
                          stroke="#F59E0B"
                          strokeWidth={1}
                          dot={{ fill: '#F59E0B', r: 4 }}
                          name="누적 비율 (%)"
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* 3-4행 오른쪽 - 알람 발생 타임라인 */}
                <div className="alarm-card alarm-card-4">
                  <div className="alarm-card-header">알람 발생 타임라인</div>
                  <div className="alarm-card-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart
                        data={processedTimelineData}
                        margin={{ top: 20, right: 10, left: -25, bottom: 10 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis
                          dataKey="time"
                          stroke="#9CA3AF"
                          tick={{ fill: '#9CA3AF', fontSize: 11 }}
                          axisLine={{ stroke: '#4B5563' }}
                          label={{ value: '시간', position: 'insideBottom', offset: -5, fill: '#9CA3AF', fontSize: 12 }}
                        />
                        <YAxis
                          stroke="#9CA3AF"
                          tick={{ fill: '#9CA3AF', fontSize: 11 }}
                          axisLine={{ stroke: '#4B5563' }}
                          label={{ value: '알람 수', angle: -90, position: 'center', style: { textAnchor: 'middle' }, fill: '#9CA3AF', fontSize: 11 }}
                        />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '6px', fontSize: '12px' }}
                          labelStyle={{ color: '#9CA3AF', fontSize: '13px' }}
                          itemStyle={{ color: '#F59E0B' }}
                        />
                        <Area
                          type="monotone"
                          dataKey="count"
                          fill="#F59E0B"
                          fillOpacity={0.3}
                          stroke="#F59E0B"
                          strokeWidth={1}
                          name="알람 수"
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
              ) : null}
            </div>
            );
          })()}
          {activeTab === 'SPC' && (
            <div className="spc-container">
              {/* 상단 컨트롤 영역 */}
              <div className="spc-control-panel">
                {/* 파라미터 선택 */}
                <div className="spc-control-group">
                  <label className="spc-label">측정 항목</label>
                  <select
                    value={selectedParameter}
                    onChange={(e) => setSelectedParameter(e.target.value)}
                    className="spc-select"
                  >
                    <option value="cycle_time">사이클타임</option>
                    <option value="injection_pressure">사출압력</option>
                    <option value="injection_speed">사출속도</option>
                    <option value="mold_temperature">금형온도</option>
                    <option value="cushion">쿠션량</option>
                  </select>
                </div>

                {/* 관리한계 정보 */}
                {spcData?.xbarChart?.data && spcData.xbarChart.data.length > 0 && (
                  <div className="spc-limits-group">
                    <span className="spc-limit-item">
                      <span className="limit-label">UCL</span>
                      <span className="limit-value ucl">{spcData.xbarChart.data[0]?.ucl?.toFixed(2)}</span>
                    </span>
                    <span className="spc-limit-item">
                      <span className="limit-label">CL</span>
                      <span className="limit-value cl">{spcData.xbarChart.data[0]?.cl?.toFixed(2)}</span>
                    </span>
                    <span className="spc-limit-item">
                      <span className="limit-label">LCL</span>
                      <span className="limit-value lcl">{spcData.xbarChart.data[0]?.lcl?.toFixed(2)}</span>
                    </span>
                  </div>
                )}
              </div>

              {/* 로딩 상태 */}
              {isSpcLoading && !spcData && (
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                  <p>SPC 데이터를 불러오는 중...</p>
                </div>
              )}

              {/* 에러 상태 */}
              {spcError && (
                <div className="error-container">
                  <p>⚠️ {spcError}</p>
                </div>
              )}

              {/* SPC 데이터 */}
              {!isSpcLoading && !spcError && spcData && (
              <div className="spc-grid">
                {/* 왼쪽 영역: X바 관리도 */}
                <div className="spc-left-area">
                  {/* X 관리도 */}

                  {/* X 관리도 */}
                  <div className="spc-card spc-card-12">
                    <div className="spc-card-header">X̄ 관리도 (X-bar Chart)</div>

                  <div className="spc-card-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart
                        data={spcData?.xbarChart?.data || []}
                        margin={{ top: 20, right: 10, left: -25, bottom: 10 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis
                          dataKey="group"
                          stroke="#9CA3AF"
                          tick={{ fill: '#9CA3AF', fontSize: 11 }}
                          axisLine={{ stroke: '#4B5563' }}
                          label={{ value: '샘플 그룹', position: 'insideBottom', offset: -5, fill: '#9CA3AF', fontSize: 12 }}
                        />
                        <YAxis
                          stroke="#9CA3AF"
                          tick={{ fill: '#9CA3AF', fontSize: 11 }}
                          axisLine={{ stroke: '#4B5563' }}
                          label={{ value: '값', angle: -90, position: 'center', style: { textAnchor: 'middle' }, fill: '#9CA3AF', fontSize: 11 }}
                          domain={spcData?.xbarChart?.data && spcData.xbarChart.data.length > 0 ? [
                            () => {
                              const lcl = spcData.xbarChart.data[0].lcl;
                              const range = spcData.xbarChart.data[0].ucl - lcl;
                              return lcl - range * 0.2;
                            },
                            () => {
                              const ucl = spcData.xbarChart.data[0].ucl;
                              const lcl = spcData.xbarChart.data[0].lcl;
                              const range = ucl - lcl;
                              return ucl + range * 0.2;
                            }
                          ] : ['auto', 'auto']}
                          tickFormatter={(value) => value.toFixed(1)}
                        />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '6px', fontSize: '12px' }}
                          labelStyle={{ color: '#9CA3AF', fontSize: '13px' }}
                        />
                        <Line type="monotone" dataKey="ucl" stroke="#EF4444" strokeWidth={1} strokeDasharray="5 5" dot={false} name="UCL" />
                        <Line type="monotone" dataKey="cl" stroke="#10B981" strokeWidth={1} dot={false} name="CL" />
                        <Line type="monotone" dataKey="lcl" stroke="#EF4444" strokeWidth={1} strokeDasharray="5 5" dot={false} name="LCL" />
                        <Line type="monotone" dataKey="value" stroke="#3B82F6" strokeWidth={1} dot={{ fill: '#3B82F6', r: 3 }} name="측정값" />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                </div>

                {/* 영역 3 - 공정능력지수 & 기초통계 */}
                <div className="spc-card spc-card-3">
                  <div className="spc-card-header">통계 정보</div>
                  <div className="spc-card-content">
                    <div className="spc-stats-container">
                      {/* 공정능력지수 섹션 */}
                      <div className="spc-stats-section">
                        <div className="spc-section-title">공정능력지수</div>
                        <div className="spc-stats-grid">
                          <div className="spc-stat-item">
                            <span className="spc-stat-label">Cp</span>
                            <span className="spc-stat-value">{spcData?.statistics?.processCapability?.cp?.toFixed(2) || '-'}</span>
                          </div>
                          <div className="spc-stat-item">
                            <span className="spc-stat-label">Cpk</span>
                            <span className="spc-stat-value">{spcData?.statistics?.processCapability?.cpk?.toFixed(2) || '-'}</span>
                          </div>
                          <div className="spc-stat-item">
                            <span className="spc-stat-label">Pp</span>
                            <span className="spc-stat-value">{spcData?.statistics?.processCapability?.pp?.toFixed(2) || '-'}</span>
                          </div>
                          <div className="spc-stat-item">
                            <span className="spc-stat-label">Ppk</span>
                            <span className="spc-stat-value">{spcData?.statistics?.processCapability?.ppk?.toFixed(2) || '-'}</span>
                          </div>
                          <div className="spc-stat-item">
                            <span className="spc-stat-label">불량률</span>
                            <span className="spc-stat-value">{spcData?.statistics?.processCapability?.defectRate?.toFixed(2) || '-'}%</span>
                          </div>
                        </div>
                      </div>

                      {/* 기초통계 섹션 */}
                      <div className="spc-stats-section">
                        <div className="spc-section-title">기초통계</div>
                        <div className="spc-stats-grid">
                          <div className="spc-stat-item">
                            <span className="spc-stat-label">샘플 수</span>
                            <span className="spc-stat-value">{spcData?.statistics?.basicStats?.sampleCount || '-'}</span>
                          </div>
                          <div className="spc-stat-item">
                            <span className="spc-stat-label">평균</span>
                            <span className="spc-stat-value">{spcData?.statistics?.basicStats?.mean?.toFixed(2) || '-'}</span>
                          </div>
                          <div className="spc-stat-item">
                            <span className="spc-stat-label">표준편차</span>
                            <span className="spc-stat-value">{spcData?.statistics?.basicStats?.stdDev?.toFixed(2) || '-'}</span>
                          </div>
                          <div className="spc-stat-item">
                            <span className="spc-stat-label">최소값</span>
                            <span className="spc-stat-value">{spcData?.statistics?.basicStats?.min?.toFixed(2) || '-'}</span>
                          </div>
                          <div className="spc-stat-item">
                            <span className="spc-stat-label">최대값</span>
                            <span className="spc-stat-value">{spcData?.statistics?.basicStats?.max?.toFixed(2) || '-'}</span>
                          </div>
                          <div className="spc-stat-item">
                            <span className="spc-stat-label">범위</span>
                            <span className="spc-stat-value">{spcData?.statistics?.basicStats?.range?.toFixed(2) || '-'}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 4-5 영역 합침 - R 관리도 */}
                <div className="spc-card spc-card-45">
                  <div className="spc-card-header">R 관리도 (R Chart)</div>
                  <div className="spc-card-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart
                        data={spcData?.rchart?.data || []}
                        margin={{ top: 20, right: 10, left: -25, bottom: 10 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis
                          dataKey="group"
                          stroke="#9CA3AF"
                          tick={{ fill: '#9CA3AF', fontSize: 11 }}
                          axisLine={{ stroke: '#4B5563' }}
                          label={{ value: '샘플 그룹', position: 'insideBottom', offset: -5, fill: '#9CA3AF', fontSize: 12 }}
                        />
                        <YAxis
                          stroke="#9CA3AF"
                          tick={{ fill: '#9CA3AF', fontSize: 11 }}
                          axisLine={{ stroke: '#4B5563' }}
                          label={{ value: '값', angle: -90, position: 'center', style: { textAnchor: 'middle' }, fill: '#9CA3AF', fontSize: 11 }}
                          tickFormatter={(value) => value.toFixed(1)}
                        />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '6px', fontSize: '12px' }}
                          labelStyle={{ color: '#9CA3AF', fontSize: '13px' }}
                        />
                        <Line type="monotone" dataKey="ucl" stroke="#EF4444" strokeWidth={1} strokeDasharray="5 5" dot={false} name="UCL" />
                        <Line type="monotone" dataKey="cl" stroke="#10B981" strokeWidth={1} dot={false} name="CL" />
                        <Line type="monotone" dataKey="lcl" stroke="#EF4444" strokeWidth={1} strokeDasharray="5 5" dot={false} name="LCL" />
                        <Line type="monotone" dataKey="value" stroke="#F59E0B" strokeWidth={1} dot={{ fill: '#F59E0B', r: 3 }} name="범위값" />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* 영역 6 - 이상점 검출 */}
                <div className="spc-card spc-card-6">
                  <div className="spc-card-header">이상점 검출</div>
                  <div className="spc-card-content">
                    <div className="spc-anomaly-table-container">
                      <table className="spc-anomaly-table">
                        <thead>
                          <tr>
                            <th>시간</th>
                            <th>값</th>
                            <th>타입</th>
                          </tr>
                        </thead>
                        <tbody>
                          {spcData?.anomalies && spcData.anomalies.length > 0 ? (
                            spcData.anomalies.map((anomaly, index) => (
                              <tr key={index}>
                                <td>{anomaly.time}</td>
                                <td>{anomaly.value?.toFixed(2)}</td>
                                <td>
                                  <span className={`anomaly-badge ${anomaly.type?.includes('UCL') ? 'ucl' : anomaly.type?.includes('LCL') ? 'lcl' : 'trend'}`}>
                                    {anomaly.type}
                                  </span>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="3" style={{ textAlign: 'center', color: '#9CA3AF' }}>
                                이상점이 검출되지 않았습니다
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
              )}
            </div>
          )}

          {/* 양/불 판정 탭 */}
          {activeTab === '양/불' && (
            <div className={showLSTMAnalysis ? 'lstm-analysis-container' : 'pass-fail-grid'}>
              {!showLSTMAnalysis && (
                <>
              {/* 좌측 영역 - 사출정보 */}
              <div className="overview-card">
                <div className="overview-section">
                  <h3 className="section-title">사출정보</h3>

                  {/* 제품 이미지 */}
                  <div style={{
                    width: '100%',
                    paddingTop: '75%',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #30363D',
                    borderRadius: '8px',
                    marginBottom: '12px',
                    overflow: 'hidden',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <img
                      src="/product-blueprint.png"
                      alt="제품 이미지"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain'
                      }}
                    />
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      display: 'none',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      color: '#6B7280'
                    }}>
                      <span className="material-symbols-outlined" style={{ fontSize: '48px' }}>image</span>
                      <span style={{ fontSize: '12px' }}>이미지 없음</span>
                    </div>
                  </div>

                  {/* 제품 정보 */}
                  <div className="info-list" style={{ marginBottom: '12px' }}>
                    <div className="info-item" style={{ padding: '4px 12px', minHeight: '32px' }}>
                      <span className="info-label">제품번호</span>
                      <span className="info-value">-</span>
                    </div>
                    <div className="info-item" style={{ padding: '4px 12px', minHeight: '32px' }}>
                      <span className="info-label">제품명</span>
                      <span className="info-value">{
                        selectedShotNumber && shotHistoryData.find(s => s.shotNum === selectedShotNumber)?.craftId || '-'
                      }</span>
                    </div>
                  </div>

                  {/* 셋팅값 표 */}
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: '13px'
                    }}>
                      <thead>
                        <tr style={{
                          backgroundColor: '#1a1f2e',
                          borderBottom: '1px solid #30363D'
                        }}>
                          <th style={{
                            padding: '10px 8px',
                            textAlign: 'left',
                            color: '#9CA3AF',
                            fontWeight: '600'
                          }}>항목</th>
                          <th style={{
                            padding: '10px 8px',
                            textAlign: 'right',
                            color: '#9CA3AF',
                            fontWeight: '600'
                          }}>셋팅값</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr style={{ borderBottom: '1px solid #30363D' }}>
                          <td style={{ padding: '10px 8px', color: '#E5E7EB' }}>계량위치</td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', color: '#60A5FA' }}>5.0 mm</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #30363D' }}>
                          <td style={{ padding: '10px 8px', color: '#E5E7EB' }}>사출위치</td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', color: '#60A5FA' }}>12 mm</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #30363D' }}>
                          <td style={{ padding: '10px 8px', color: '#E5E7EB' }}>히터온도</td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', color: '#60A5FA' }}>220 °C</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #30363D' }}>
                          <td style={{ padding: '10px 8px', color: '#E5E7EB' }}>사출속도</td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', color: '#60A5FA' }}>30 mm/s</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #30363D' }}>
                          <td style={{ padding: '10px 8px', color: '#E5E7EB' }}>쿠션량</td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', color: '#60A5FA' }}>7.7 mm</td>
                        </tr>
                        <tr>
                          <td style={{ padding: '10px 8px', color: '#E5E7EB' }}>사출시간</td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', color: '#60A5FA' }}>5.3 s</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* 중앙 영역 - Shot History */}
              <div className="overview-card">
                <div className="overview-section">
                  <h3 className="section-title">Shot History</h3>

                  <div className="hide-scrollbar" style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 250px)' }}>
                    <table style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: '12px'
                    }}>
                      <thead style={{ position: 'sticky', top: 0, backgroundColor: '#1a1f2e', zIndex: 1 }}>
                        <tr style={{
                          borderBottom: '1px solid #30363D'
                        }}>
                          <th style={{
                            padding: '8px 6px',
                            textAlign: 'center',
                            color: '#9CA3AF',
                            fontWeight: '600',
                            borderRight: '1px solid #30363D'
                          }}>Shot</th>
                          <th style={{
                            padding: '8px 6px',
                            textAlign: 'center',
                            color: '#9CA3AF',
                            fontWeight: '600',
                            borderRight: '1px solid #30363D'
                          }}>시간</th>
                          <th style={{
                            padding: '8px 6px',
                            textAlign: 'center',
                            color: '#9CA3AF',
                            fontWeight: '600',
                            borderRight: '1px solid #30363D'
                          }}>계량위치</th>
                          <th style={{
                            padding: '8px 6px',
                            textAlign: 'center',
                            color: '#9CA3AF',
                            fontWeight: '600',
                            borderRight: '1px solid #30363D'
                          }}>사출위치</th>
                          <th style={{
                            padding: '8px 6px',
                            textAlign: 'center',
                            color: '#9CA3AF',
                            fontWeight: '600',
                            borderRight: '1px solid #30363D'
                          }}>히터온도</th>
                          <th style={{
                            padding: '8px 6px',
                            textAlign: 'center',
                            color: '#9CA3AF',
                            fontWeight: '600',
                            borderRight: '1px solid #30363D'
                          }}>사출속도</th>
                          <th style={{
                            padding: '8px 6px',
                            textAlign: 'center',
                            color: '#9CA3AF',
                            fontWeight: '600',
                            borderRight: '1px solid #30363D'
                          }}>쿠션량</th>
                          <th style={{
                            padding: '8px 6px',
                            textAlign: 'center',
                            color: '#9CA3AF',
                            fontWeight: '600',
                            borderRight: '1px solid #30363D'
                          }}>사출시간</th>
                          <th style={{
                            padding: '8px 6px',
                            textAlign: 'center',
                            color: '#9CA3AF',
                            fontWeight: '600'
                          }}>양/불</th>
                        </tr>
                      </thead>
                      <tbody>
                        {isShotHistoryLoading ? (
                          <tr>
                            <td colSpan="9" style={{ padding: '40px', textAlign: 'center', color: '#9CA3AF' }}>
                              데이터 로딩 중...
                            </td>
                          </tr>
                        ) : shotHistoryError ? (
                          <tr>
                            <td colSpan="9" style={{ padding: '40px', textAlign: 'center', color: '#EF4444' }}>
                              {shotHistoryError}
                            </td>
                          </tr>
                        ) : shotHistoryData.length === 0 ? (
                          <tr>
                            <td colSpan="9" style={{ padding: '40px', textAlign: 'center', color: '#9CA3AF' }}>
                              샷 이력 데이터가 없습니다.
                            </td>
                          </tr>
                        ) : shotHistoryData.map((shotData, index) => {
                          const { shotNum, shotTime, qualityStatus, metering, injection, temperature, speed, cushion, injectionTime } = shotData;
                          const isSelected = selectedShotNumber === shotNum;
                          // shotTime (ISO 문자열) → HH:MM:SS
                          let shotTimeStr = '-';
                          if (shotTime) {
                            const dt = new Date(shotTime);
                            if (!isNaN(dt.getTime())) {
                              shotTimeStr = dt.toLocaleTimeString('ko-KR', { hour12: false });
                            }
                          }

                          return (
                            <tr
                              key={shotNum}
                              onClick={() => setSelectedShotNumber(shotNum)}
                              style={{
                                borderBottom: '1px solid #30363D',
                                backgroundColor: isSelected
                                  ? 'rgba(59, 130, 246, 0.15)'
                                  : (index % 2 === 0 ? 'transparent' : 'rgba(255, 255, 255, 0.02)'),
                                cursor: 'pointer',
                                transition: 'background-color 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                if (!isSelected) {
                                  e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.08)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!isSelected) {
                                  e.currentTarget.style.backgroundColor = index % 2 === 0 ? 'transparent' : 'rgba(255, 255, 255, 0.02)';
                                }
                              }}
                            >
                              <td style={{
                                padding: '8px 6px',
                                textAlign: 'center',
                                color: isSelected ? '#60A5FA' : '#E5E7EB',
                                borderRight: '1px solid #30363D',
                                fontWeight: isSelected ? '600' : '500'
                              }}>#{shotNum}</td>
                              <td style={{
                                padding: '8px 6px',
                                textAlign: 'center',
                                color: '#9CA3AF',
                                borderRight: '1px solid #30363D',
                                fontFamily: 'monospace',
                                fontSize: '11px'
                              }}>{shotTimeStr}</td>
                              <td style={{
                                padding: '8px 6px',
                                textAlign: 'center',
                                color: '#60A5FA',
                                borderRight: '1px solid #30363D'
                              }}>{metering != null ? metering.toFixed(1) : '-'}</td>
                              <td style={{
                                padding: '8px 6px',
                                textAlign: 'center',
                                color: '#60A5FA',
                                borderRight: '1px solid #30363D'
                              }}>{injection != null ? injection.toFixed(1) : '-'}</td>
                              <td style={{
                                padding: '8px 6px',
                                textAlign: 'center',
                                color: '#60A5FA',
                                borderRight: '1px solid #30363D'
                              }}>{temperature != null ? temperature.toFixed(0) : '-'}</td>
                              <td style={{
                                padding: '8px 6px',
                                textAlign: 'center',
                                color: '#60A5FA',
                                borderRight: '1px solid #30363D'
                              }}>{speed != null ? speed.toFixed(0) : '-'}</td>
                              <td style={{
                                padding: '8px 6px',
                                textAlign: 'center',
                                color: '#60A5FA',
                                borderRight: '1px solid #30363D'
                              }}>{cushion != null ? cushion.toFixed(1) : '-'}</td>
                              <td style={{
                                padding: '8px 6px',
                                textAlign: 'center',
                                color: '#60A5FA',
                                borderRight: '1px solid #30363D'
                              }}>{injectionTime != null ? injectionTime.toFixed(2) : '-'}</td>
                              <td style={{
                                padding: '8px 6px',
                                textAlign: 'center'
                              }}>
                                <span style={{
                                  display: 'inline-block',
                                  padding: '2px 8px',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  backgroundColor: qualityStatus === 'GOOD'
                                    ? 'rgba(34, 197, 94, 0.2)'
                                    : (qualityStatus === 'BAD' || qualityStatus === 'DEFECT')
                                      ? 'rgba(239, 68, 68, 0.2)'
                                      : 'rgba(156, 163, 175, 0.2)',
                                  color: qualityStatus === 'GOOD'
                                    ? '#22C55E'
                                    : (qualityStatus === 'BAD' || qualityStatus === 'DEFECT')
                                      ? '#EF4444'
                                      : '#9CA3AF'
                                }}>
                                  {qualityStatus === 'GOOD' ? '양' : (qualityStatus === 'BAD' || qualityStatus === 'DEFECT') ? '불' : '미'}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* 우측 영역 - 레이더 차트 */}
              <div className="overview-card" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <div className="overview-section" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                  <h3 className="section-title">설정값 vs 실측값 비교</h3>

                  {/* 레이더 차트 영역 */}
                  <div style={{ position: 'relative', flex: '1 1 auto', minHeight: 0 }}>
                    {/* Shot 번호 표시 */}
                    {selectedShotNumber && (
                      <div style={{
                        position: 'absolute',
                        top: '10px',
                        left: '10px',
                        zIndex: 10
                      }}>
                        <table style={{
                          borderCollapse: 'collapse',
                          fontSize: '12px',
                          backgroundColor: '#1a1f2e',
                          border: '1px solid #30363D',
                          borderRadius: '6px',
                          overflow: 'hidden'
                        }}>
                          <thead>
                            <tr style={{ backgroundColor: '#252a31' }}>
                              <th style={{
                                padding: '8px 12px',
                                textAlign: 'center',
                                color: '#9CA3AF',
                                fontWeight: '600',
                                fontSize: '11px'
                              }}>Shot</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td style={{
                                padding: '8px 12px',
                                textAlign: 'center',
                                color: '#60A5FA',
                                fontWeight: '600',
                                fontSize: '16px'
                              }}>#{selectedShotNumber}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )}

                    <ResponsiveContainer width="100%" height="100%" key={selectedShotNumber}>
                    <RadarChart data={(() => {
                      const selectedShot = selectedShotNumber
                        ? shotHistoryData.find(s => s.shotNum === selectedShotNumber)
                        : null;

                      // 설정값 (고정) - 실제 사출기 데이터 기준
                      const settings = {
                        metering: 34.1,      // 계량위치 (mm)
                        injection: 34.1,     // 사출시작위치 (mm)
                        temperature: 220,    // 히터온도 (°C)
                        speed: 30,           // 사출속도 (mm/s)
                        cushion: 7.7,        // 쿠션량 (mm)
                        time: 5.3            // 사출시간 (sec)
                      };

                      // 실측값 (선택된 샷이 있으면 해당 샷 데이터, 없으면 기본값)
                      const measured = selectedShot ? {
                        metering: selectedShot.metering,
                        injection: selectedShot.injection,
                        temperature: selectedShot.temperature,
                        speed: selectedShot.speed,
                        cushion: selectedShot.cushion,
                        time: selectedShot.injectionTime
                      } : {
                        metering: 34.1,
                        injection: 34.1,
                        temperature: 220,
                        speed: 30,
                        cushion: 7.7,
                        time: 5.3
                      };

                      // 정규화 함수 (설정값을 중심으로 70이 되도록 변환)
                      const normalizeAroundSetting = (value, settingValue, range) => {
                        // settingValue를 중심으로 ±range만큼을 0-100으로 매핑
                        // settingValue가 정확히 70이 되도록 함
                        const deviation = value - settingValue;  // 설정값과의 차이
                        const normalizedDeviation = (deviation / range) * 30;  // ±30 범위로 정규화
                        return 70 + normalizedDeviation;  // 70을 중심으로 ±30
                      };

                      return [
                        {
                          subject: '계량위치',
                          설정값: 70,  // 항상 70
                          실측값: normalizeAroundSetting(measured.metering, settings.metering, 5),
                          fullMark: 100
                        },
                        {
                          subject: '사출위치',
                          설정값: 70,  // 항상 70
                          실측값: normalizeAroundSetting(measured.injection, settings.injection, 10),
                          fullMark: 100
                        },
                        {
                          subject: '히터온도',
                          설정값: 70,  // 항상 70
                          실측값: normalizeAroundSetting(measured.temperature, settings.temperature, 20),
                          fullMark: 100
                        },
                        {
                          subject: '사출속도',
                          설정값: 70,  // 항상 70
                          실측값: normalizeAroundSetting(measured.speed, settings.speed, 30),
                          fullMark: 100
                        },
                        {
                          subject: '쿠션량',
                          설정값: 70,  // 항상 70
                          실측값: normalizeAroundSetting(measured.cushion, settings.cushion, 5),
                          fullMark: 100
                        },
                        {
                          subject: '사출시간',
                          설정값: 70,  // 항상 70
                          실측값: normalizeAroundSetting(measured.time, settings.time, 5),
                          fullMark: 100
                        }
                      ];
                    })()}>
                      <PolarGrid stroke="#30363D" />
                      <PolarAngleAxis
                        dataKey="subject"
                        tick={{ fill: '#9CA3AF', fontSize: 13 }}
                      />
                      <PolarRadiusAxis
                        angle={90}
                        domain={[0, 100]}
                        ticks={[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]}
                        tick={{ fill: '#6B7280', fontSize: 11 }}
                        stroke="#30363D"
                      />
                      <Radar
                        name="설정값"
                        dataKey="설정값"
                        stroke="#3B82F6"
                        fill="#3B82F6"
                        fillOpacity={0.2}
                        strokeWidth={1}
                      />
                      <Radar
                        name="실측값"
                        dataKey="실측값"
                        stroke="#10B981"
                        fill="#10B981"
                        fillOpacity={0.2}
                        strokeWidth={1}
                      />
                      <Legend
                        align="right"
                        verticalAlign="top"
                        wrapperStyle={{
                          fontSize: '12px',
                          paddingTop: '10px',
                          paddingRight: '20px'
                        }}
                        iconSize={8}
                        iconType="circle"
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#1a1f2e',
                          border: '1px solid #30363D',
                          borderRadius: '8px',
                          color: '#E5E7EB',
                          fontSize: '12px'
                        }}
                        labelStyle={{ fontSize: '13px' }}
                      />
                    </RadarChart>
                  </ResponsiveContainer>

                    {/* 양/불 상세분석 버튼 - 차트 영역 내부 우측 하단 */}
                    <button
                      onClick={() => setShowLSTMAnalysis(!showLSTMAnalysis)}
                      style={{
                        position: 'absolute',
                        bottom: '10px',
                        right: '20px',
                        zIndex: 10,
                        padding: '4px 12px',
                        backgroundColor: showLSTMAnalysis ? '#3B82F6' : 'rgba(59, 130, 246, 0.2)',
                        color: showLSTMAnalysis ? '#FFFFFF' : '#3B82F6',
                        border: '1px solid #3B82F6',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                      onMouseEnter={(e) => {
                        if (!showLSTMAnalysis) {
                          e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.3)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!showLSTMAnalysis) {
                          e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.2)';
                        }
                      }}
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>
                        psychology
                      </span>
                      LSTM 불량 원인 분석
                    </button>
                  </div>

                  {/* 실측값 및 양/불 판정 표시 */}
                  <div style={{ marginTop: '8px', padding: '0 20px', display: 'flex', gap: '12px', flex: '0 0 auto' }}>
                    {(() => {
                      const selectedShot = selectedShotNumber
                        ? shotHistoryData.find(s => s.shotNum === selectedShotNumber)
                        : null;

                      const displayData = selectedShot || {
                        metering: 45.2,
                        injection: 120.5,
                        temperature: 230,
                        speed: 85,
                        cushion: 5.8,
                        injectionTime: 2.5,
                        qualityStatus: 'GOOD'
                      };

                      return (
                        <>
                          {/* 실측값 표 */}
                          <table style={{
                            flex: '1',
                            borderCollapse: 'collapse',
                            fontSize: '12px',
                            backgroundColor: '#1a1f2e',
                            border: '1px solid #30363D',
                            borderRadius: '6px',
                            overflow: 'hidden'
                          }}>
                            <thead>
                              <tr style={{ backgroundColor: '#252a31' }}>
                                <th style={{ padding: '10px', textAlign: 'center', color: '#9CA3AF', fontWeight: '600', borderRight: '1px solid #30363D' }}>계량위치</th>
                                <th style={{ padding: '10px', textAlign: 'center', color: '#9CA3AF', fontWeight: '600', borderRight: '1px solid #30363D' }}>사출위치</th>
                                <th style={{ padding: '10px', textAlign: 'center', color: '#9CA3AF', fontWeight: '600', borderRight: '1px solid #30363D' }}>히터온도</th>
                                <th style={{ padding: '10px', textAlign: 'center', color: '#9CA3AF', fontWeight: '600', borderRight: '1px solid #30363D' }}>사출속도</th>
                                <th style={{ padding: '10px', textAlign: 'center', color: '#9CA3AF', fontWeight: '600', borderRight: '1px solid #30363D' }}>쿠션량</th>
                                <th style={{ padding: '10px', textAlign: 'center', color: '#9CA3AF', fontWeight: '600' }}>사출시간</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td style={{ padding: '10px', textAlign: 'center', color: '#10B981', fontWeight: '500', borderRight: '1px solid #30363D' }}>{Number(displayData.metering).toFixed(1)}mm</td>
                                <td style={{ padding: '10px', textAlign: 'center', color: '#10B981', fontWeight: '500', borderRight: '1px solid #30363D' }}>{Number(displayData.injection).toFixed(1)}mm</td>
                                <td style={{ padding: '10px', textAlign: 'center', color: '#10B981', fontWeight: '500', borderRight: '1px solid #30363D' }}>{Number(displayData.temperature).toFixed(1)}°C</td>
                                <td style={{ padding: '10px', textAlign: 'center', color: '#10B981', fontWeight: '500', borderRight: '1px solid #30363D' }}>{Number(displayData.speed).toFixed(1)}mm/s</td>
                                <td style={{ padding: '10px', textAlign: 'center', color: '#10B981', fontWeight: '500', borderRight: '1px solid #30363D' }}>{Number(displayData.cushion).toFixed(1)}mm</td>
                                <td style={{ padding: '10px', textAlign: 'center', color: '#10B981', fontWeight: '500' }}>{Number(displayData.injectionTime).toFixed(1)}s</td>
                              </tr>
                            </tbody>
                          </table>

                          {/* 양/불 판정 표 */}
                          <table style={{
                            width: '150px',
                            borderCollapse: 'collapse',
                            fontSize: '12px',
                            backgroundColor: '#1a1f2e',
                            border: '1px solid #30363D',
                            borderRadius: '6px',
                            overflow: 'hidden'
                          }}>
                            <thead>
                              <tr style={{ backgroundColor: '#252a31' }}>
                                <th style={{ padding: '10px', textAlign: 'center', color: '#9CA3AF', fontWeight: '600' }}>양/불</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td style={{ padding: '10px', textAlign: 'center' }}>
                                  <span style={{
                                    display: 'inline-block',
                                    padding: '4px 16px',
                                    borderRadius: '4px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    backgroundColor: displayData.qualityStatus === 'GOOD'
                                      ? 'rgba(34, 197, 94, 0.2)'
                                      : (displayData.qualityStatus === 'BAD' || displayData.qualityStatus === 'DEFECT')
                                        ? 'rgba(239, 68, 68, 0.2)'
                                        : 'rgba(156, 163, 175, 0.2)',
                                    color: displayData.qualityStatus === 'GOOD'
                                      ? '#22C55E'
                                      : (displayData.qualityStatus === 'BAD' || displayData.qualityStatus === 'DEFECT')
                                        ? '#EF4444'
                                        : '#9CA3AF'
                                  }}>
                                    {displayData.qualityStatus === 'GOOD' ? '양품' : (displayData.qualityStatus === 'BAD' || displayData.qualityStatus === 'DEFECT') ? '불량' : '미판정'}
                                  </span>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
              </>
              )}

              {/* LSTM 불량 원인 분석 */}
              {showLSTMAnalysis && (
                <>
                  <button
                    onClick={() => setShowLSTMAnalysis(false)}
                    style={{
                      position: 'absolute',
                      top: '10px',
                      right: '20px',
                      zIndex: 100,
                      padding: '8px 16px',
                      backgroundColor: 'rgba(29, 33, 39, 0.95)',
                      color: '#E5E7EB',
                      border: '1px solid #30363D',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(48, 54, 61, 0.95)';
                      e.currentTarget.style.color = '#FFFFFF';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(29, 33, 39, 0.95)';
                      e.currentTarget.style.color = '#E5E7EB';
                    }}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>arrow_back</span>
                    뒤로가기
                  </button>
                  <LSTMDefectAnalysis
                    selectedShotNumber={selectedShotNumber}
                    shotHistoryData={shotHistoryData}
                    equipmentId={selectedMachineId || machine?.id}
                    craftId={selectedShotNumber ? shotHistoryData.find(s => s.shotNum === selectedShotNumber)?.craftId : shotHistoryData[0]?.craftId || ''}
                  />
                </>
              )}
            </div>
          )}

          {/* 감시1 탭 */}
          {activeTab === '감시1' && (
            <MonitoringWatch1
              equipmentId={selectedMachineId || machine?.id}
              craftId={machine?.productName}
              machineData={machineData}
            />
          )}

          {/* 감시2 탭 */}
          {activeTab === '감시2' && (
            <MonitoringWatch2
              equipmentId={selectedMachineId || machine?.id}
              craftId={machine?.productName}
              machineData={machineData}
            />
          )}

          {activeTab !== '대시보드' && activeTab !== 'Overview' && activeTab !== 'Cycle' && activeTab !== '온도' && activeTab !== '구동부' && activeTab !== '알람' && activeTab !== 'SPC' && activeTab !== '양/불' && activeTab !== '감시1' && activeTab !== '감시2' && (
            <div style={{ padding: '20px', color: '#9CA3AF' }}>
              {activeTab} 컨텐츠 영역 (추후 추가 예정)
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}

export default Machine;
